import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  Eyebrow,
  Heading,
  Alert,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Skeleton,
  StatusBadge,
  StatusFilterCard,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  type StatusFilterAccent,
  type StatusKey,
} from '@insolech/ui';
import { ClipboardList, Plus, Download, Eye, Copy, Trash2, FileSpreadsheet, Mail, MoreVertical, Search, X, QrCode, Flag, CalendarDays, List } from 'lucide-react';
import {
  adminQueries,
  lineProviderAssignmentQueries,
  orderMutations,
  orderQueries,
  supplierDraftQueries,
  type AdminProviderRow,
  type LineProviderAssignmentRow,
  type LocalChainStage,
  type OrderListRow,
} from '@insolech/sdk';
import {
  exportOrderToExcel,
  getPrescriptionExcelBytes,
  prescriptionExcelFilename,
} from './prescriptionExcelExport';
import { WorkshopPdfButton, downloadWorkshopPdf } from './workshop-pdf/WorkshopPdfButton';
import { Button } from '@insolech/ui';
import { AppShell } from '@/components/layout/AppShell';
import { supabase } from '@/lib/supabase';
import { useAnnouncedDateGuard } from './useAnnouncedDateGuard';
import { useAuth } from '@/features/auth/AuthContext';
import { LpoEmailButton } from './lpo-email/LpoEmailButton';
import { RecycleBinButton } from './RecycleBinButton';
import { OrdersCalendarView, calendarListParams } from './calendar/OrdersCalendarView';
import { ColumnFilterPopover, type ColumnFilterOption } from './ColumnFilterPopover';
import { StrandedDraftsTable } from './StrandedDraftsTable';
import { formatProductLabel, resolvePatientDisplay } from '@/features/orders/productLabel';
import { buildDraftVariables } from '@/features/orders/draftEmailVariables';

/**
 * OrderTrackerPage - shared component for `/orders` (podiatrist) and
 * `/admin/orders` (admin).
 *
 * Per `phase4-input/ux-feedback.md`:
 *   - Status filter tabs: Pending / Submitted / Production / Delivery /
 *     Delivered / Active (default) / Total
 *   - Admin sees 9 columns, podiatrist sees 6
 *   - Filter state persists in URL query string
 *   - Search by patient name OR short code (debounced)
 *   - Sort by any column header
 *   - Pagination: 25/page admin, 10/page podiatrist
 *   - Click row → navigate to detail
 */

// R37 - the status row is now a strip of seven coloured count boxes (one
// per filter key) that mirror the dashboard's KPI cards. Order matches the
// reference image: Active first (default), then Total, then the five
// individual statuses left-to-right along the production timeline.
export const FILTER_TABS: ReadonlyArray<{
  key: orderQueries.OrderStatusFilter;
  label: string;
  accent: StatusFilterAccent;
}> = [
  { key: 'active', label: 'Active', accent: 'brand' },
  { key: 'total', label: 'Total', accent: 'muted' },
  { key: 'pending', label: 'Pending', accent: 'sand' },
  { key: 'submitted', label: 'Submitted', accent: 'info' },
  { key: 'production', label: 'Production', accent: 'clay' },
  { key: 'delivery', label: 'Delivery', accent: 'warning' },
  { key: 'delivered', label: 'Delivered', accent: 'success' },
];

// Task B2: All is now the default family tab and lives on the left of the
// segmented switch. Custom-made + Ready-made follow.
const FAMILY_TABS: ReadonlyArray<{
  key: orderQueries.OrderFamilyFilter;
  labelKey: string;
}> = [
  { key: 'all', labelKey: 'orders:family_tab_all' },
  { key: 'custom', labelKey: 'orders:family_tab_custom' },
  { key: 'ots', labelKey: 'orders:family_tab_ots' },
];

interface Props {
  scope: 'admin' | 'podiatrist' | 'agent';
  /**
   * R20+ when false, render the page body WITHOUT the outer AppShell
   * wrapper. Used by the consolidated /agent tabbed page so we don't get
   * a double shell. Defaults to true so every existing caller keeps the
   * same behaviour.
   */
  withShell?: boolean;
}

const STATUS_TO_KEY: Record<string, StatusKey> = {
  draft: 'draft',
  submitted: 'submitted',
  in_review: 'in_review',
  in_production: 'in_production',
  in_delivery: 'in_delivery',
  delivered: 'delivered',
  cancelled: 'cancelled',
  returned: 'returned',
  on_hold: 'on_hold',
  assigned_to_provider: 'assigned_to_provider',
  quality_check: 'quality_check',
};

/**
 * Synthetic value used by the Clinic column filter to represent
 * direct-billed orders. The clinic FILTER_EXTRACTOR returns this string
 * (instead of the real clinic name) for any row where is_direct_billed is
 * true, so the popover lists "Direct Billing" as a selectable option
 * alongside the real clinics (selecting it filters to direct-billed rows,
 * selecting a real clinic excludes them).
 */
const DIRECT_BILLING_FILTER_VALUE = 'Direct Billing';

/**
 * Total units/pairs across ALL of an order's lines (marketplace orders carry
 * one line per cart entry, so primary_line alone would understate). Matches
 * the summing the prescription Excel banner uses. Null when the row has no
 * lines at all (legacy edge), rendered as "-".
 */
function lineQtyTotal(row: OrderListRow): number | null {
  if (row.lines.length === 0) return null;
  return row.lines.reduce((sum, l) => sum + (l.quantity ?? 1), 0);
}

/**
 * Columns that still offer a value checklist.
 *
 * Order ID and Patient were removed on 2026-08-10: the header search matches
 * both across the whole dataset, and their popovers were the misleading half
 * of "two things that look like search" - Order ID's list was hard-coded
 * empty, so it answered "No matches." for every order that exists.
 *
 * A key absent from this set is ignored when parsing ?filters=, so a
 * bookmarked URL from before cannot narrow the table through a control that
 * is no longer on screen.
 */
const FILTERABLE_COLUMNS = new Set(['status', 'podiatrist', 'clinic', 'product', 'provider']);

/**
 * What the checklists are actually built from, said plainly under the list.
 *
 * The values come from the orders CURRENTLY LOADED, because that is the truth
 * and the alternative (widening the fetch when a dropdown opens) was measured
 * in review to blank the table and disable the pager while the bigger fetch
 * was in flight. Rafael's own instruction covers this case: say plainly that
 * it only filters the loaded page.
 */
const LOADED_VALUES_NOTE =
  'Built from the orders on screen, so a value can be missing if it is on another page. To find a specific order, use the search box at the top.';
/** Status is the exception: its list is the full set, not row-derived. */
const ALL_STATUSES_NOTE = 'Every status, including ones with no orders right now.';
/** Both retired columns point at the control that does their old job properly. */
const USE_PAGE_SEARCH_NOTE =
  'To find one, use the search box at the top of this page. It matches every order, not only the ones on this page.';

export function OrderTrackerPage({ scope, withShell = true }: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation(['orders', 'common']);
  const {
    user,
    clinicId,
    clinics: userClinics,
    isAdmin,
    isAgent,
    isPodiatrist,
    profileLoading,
    // Bug-fix B: opt-in flag for "agent sees own orders". Default FALSE.
    agentIncludesOwnOrders,
  } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // R20 - agent scope joins admin scope as 'wide visibility' modes:
  // both bypass the clinic filter (RLS handles the security boundary,
  // restricting to either all clinics for admin or only acquired clinics
  // for agent). Defensive role-check downgrades to podiatrist if the
  // requested scope doesn't match the user's actual role.
  const effectiveScope: Props['scope'] =
    isAdmin && scope === 'admin' ? 'admin'
    : isAgent && scope === 'agent' ? 'agent'
    : 'podiatrist';
  // Tables/columns that show "Clinic" + "Podiatrist" + "Provider" should
  // render whenever the viewer can see across multiple clinics. Both
  // admin and agent qualify.
  const wideView = effectiveScope === 'admin' || effectiveScope === 'agent';
  // Multi-clinic podiatrist: the viewer is currently in the podiatrist
  // scope (effectiveScope === 'podiatrist') and is an active member of
  // more than one clinic. Their /orders tracker should surface every
  // order across all their memberships (not just the single
  // defaultClinicId), so we let RLS be the only narrowing layer (RLS
  // already restricts to clinic_id IN current_clinic_ids()) and we show
  // the Clinic column so they can tell which order belongs where. The
  // Podiatrist and Route columns stay hidden because they are always
  // themselves and they don't manage routing. Gating on scope rather
  // than role correctly handles dual-role users (e.g. someone with both
  // 'podiatrist' and 'agent' roles): on the /orders surface their
  // effective scope is 'podiatrist', so the multi-clinic relax applies.
  const multiClinicPodiatrist = effectiveScope === 'podiatrist' && userClinics.length > 1;
  // Combined predicate for showing the Clinic column. wideView (admin +
  // agent) already exposed it; multi-clinic podiatrists now do too.
  const showClinicColumn = wideView || multiClinicPodiatrist;
  // Agent-only surface trim. The agent variant hides the Patient
  // column, the timeline column (Expected delivery), the routing
  // column (Route), the actions cluster, and the seven status filter
  // boxes. Admin + podiatrist remain unchanged. Order ID stays as
  // the agent's single order identifier. (W4 dropped the Patient ID
  // column for every role, not just agents.)
  const isAgentView = effectiveScope === 'agent';
  // Total rendered desktop-table columns per view. Keep in lockstep with
  // the TableHead block below; used by the skeleton/error/empty full-width
  // cells so a column addition is a one-place bump.
  const totalColumns = isAgentView ? 7 : wideView ? 11 : showClinicColumn ? 9 : 8;
  // Podiatrist strict isolation: when the viewer's effective scope is
  // 'podiatrist', narrow the orders query to ones they themselves
  // authored. Without this, RLS lets through every order on every clinic
  // they belong to, including orders authored by other podiatrists at
  // the same clinic. Agent + admin scopes leave this null (those scopes
  // have their own visibility rules).
  const isPodiatristView = effectiveScope === 'podiatrist';
  const podiatristIdFilter = isPodiatristView && user?.id ? user.id : null;
  // Agent view is fully read-only: every inline editor (status pill
  // dropdown, expected-delivery picker, podiatrist / clinic / route
  // dropdowns, send toggles, action buttons) collapses to a plain text
  // label. Admin + podiatrist paths keep their existing editors. The
  // OrderRow only switches behaviour when canEditCells is false.
  const canEditCells = !isAgentView;
  // Agent-only users (agent role without a podiatrist role) cannot
  // create orders, so hide the New-order CTA in the page header on
  // top of the AppShell-level gate.
  const canCreateOrders = !(isAgent && !isPodiatrist);

  // Stalled-drafts mode (admin only): `status=stranded` in the URL flips the
  // table to the admin_stranded_drafts view (drafts older than 24h with zero
  // status-history rows). 'stranded' is intentionally NOT an
  // OrderStatusFilter member: the regular list/count queries fall back to
  // 'active' while the mode is on, and any unknown status value degrades to
  // 'active' instead of crashing on the ENUM_BY_FILTER lookup downstream.
  const rawStatus = searchParams.get('status') ?? 'active';
  const strandedMode = rawStatus === 'stranded' && effectiveScope === 'admin';
  const status: orderQueries.OrderStatusFilter = FILTER_TABS.some((tab) => tab.key === rawStatus)
    ? (rawStatus as orderQueries.OrderStatusFilter)
    : 'active';
  // Task B2: default family is now 'all' (was 'custom'), matching the new
  // leftmost tab position.
  const family =
    (searchParams.get('family') as orderQueries.OrderFamilyFilter | null) ?? 'all';
  const page = Number(searchParams.get('page') ?? '0') || 0;
  const sortBy =
    (searchParams.get('sort') as orderQueries.OrderListParams['sortBy']) ?? 'created_at';
  const sortDir = (searchParams.get('dir') as 'asc' | 'desc') ?? 'desc';

  const PAGE_SIZE_OPTIONS = [10, 25, 50, 75, 100] as const;
  const pageSizeFromUrl = Number(searchParams.get('size')) || 0;
  const pageSize: number = (PAGE_SIZE_OPTIONS as readonly number[]).includes(pageSizeFromUrl)
    ? pageSizeFromUrl
    : 25;

  const setParam = (k: string, v: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (v == null || v === '') next.delete(k);
    else next.set(k, v);
    setSearchParams(next, { replace: true });
  };

  // R22 - multi-param setter. The single-param setParam() captures
  // `searchParams` at call time; calling it twice in a row (e.g. set
  // family + reset page) silently wipes the first change because the
  // second call uses the stale snapshot. setParams atomically applies
  // a batch of updates so the URL reflects all of them.
  const setParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patch)) {
      if (v == null || v === '') next.delete(k);
      else next.set(k, v);
    }
    setSearchParams(next, { replace: true });
  };

  // R28 - Excel-style per-column filters. State is stored in the URL as a
  // JSON-encoded `filters` param (e.g. {"patient":["Ahmed Hassan"]}).
  // When any filter is active we fetch a wider set (up to 5000 rows for
  // this clinic/status/family/search context) and apply filters + paginate
  // client-side, so the popover sees the full dataset, not just one page.
  const columnFilters = React.useMemo<Record<string, string[]>>(() => {
    const raw = searchParams.get('filters');
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return {};
      const out: Record<string, string[]> = {};
      for (const [k, v] of Object.entries(parsed)) {
        // Only keys we can still apply: a retired column (patient) in a
        // bookmarked URL must not narrow the table with no visible control.
        if (!FILTERABLE_COLUMNS.has(k)) continue;
        if (Array.isArray(v) && v.length > 0) {
          out[k] = (v as unknown[]).map(String);
        }
      }
      return out;
    } catch {
      return {};
    }
  }, [searchParams]);
  const filtersActive = Object.keys(columnFilters).length > 0;

  // Mobile search (tracker field feedback): free-text across patient name +
  // order number. Lives in the `q` URL param (setParams navigates with
  // replace:true, so typing never spams history) and joins the SAME
  // client-side filter pipeline the column filters use - the card list and
  // the table always agree. The input itself renders only below sm; the
  // param still applies everywhere so the two surfaces cannot diverge.
  // Raw value feeds the controlled input (trimming it would eat the space
  // while typing "Ahmed H" and break multi-word search); the trimmed term
  // drives matching and the active flag.
  const searchTermRaw = searchParams.get('q') ?? '';
  const searchTerm = searchTermRaw.trim();
  const searchActive = searchTerm.length > 0;
  /** Any client-side row filtering active (column filters OR mobile search):
   *  flips the query to the wide fetch + client pagination mode. */
  // Search is NOT part of this any more: it is resolved server-side across
  // the whole dataset, so it keeps normal pagination and a true count instead
  // of pulling 5,000 rows into the browser. Only column filters stay local.
  const clientFilterMode = filtersActive;

  /**
   * The wrong door opens the right one: send the typed text to the page
   * search instead of leaving the admin at a dead end.
   *
   * It clears the status tab, the family tab AND any column filter, because
   * the button says "search ALL orders" - leaving a filter on would let the
   * handoff land on an empty table blaming the search term for an order that
   * exists, which is the exact failure this whole change removes.
   */
  const handoffToSearch = (term: string) => {
    setParams({ q: term, status: 'total', family: null, filters: null, page: null });
  };

  const setColumnFilter = (col: string, values: string[]) => {
    const next = { ...columnFilters };
    if (values.length === 0) delete next[col];
    else next[col] = values;
    const serialized = Object.keys(next).length === 0 ? null : JSON.stringify(next);
    setParams({ filters: serialized, page: null });
  };

  // Bug-fix B: agent surface narrows in the application layer instead of
  // relying on RLS alone. RLS unions current_clinic_ids() with
  // agent_acquired_clinic_ids(), so an agent who is also a clinic_member
  // of a clinic X would otherwise see clinic X's orders alongside their
  // acquired clinics. We resolve the acquired clinic id list here and
  // pass it as agentScope to the SDK; the SDK applies .in('clinic_id',
  // ids) + (by default) .neq('podiatrist_id', agentId).
  const agentAcquiredClinicsQuery = useQuery({
    queryKey: ['agent', 'acquired-clinic-ids', user?.id ?? null],
    enabled: isAgentView && !!user?.id,
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase
        .from('clinics')
        .select('id')
        .eq('acquired_by_agent_id', user!.id);
      if (error) throw error;
      return (data ?? []).map((c) => String((c as { id: string }).id));
    },
  });

  const agentAcquiredClinicIds = agentAcquiredClinicsQuery.data ?? [];
  // Stable identity so the agentScope object only changes when the
  // underlying list actually changes (otherwise every render produces a
  // fresh array and re-invalidates the orders query).
  const agentAcquiredClinicIdsKey = agentAcquiredClinicIds.slice().sort().join(',');
  const agentScope: orderQueries.AgentScope | null = React.useMemo(() => {
    if (!isAgentView || !user?.id) return null;
    return {
      agentId: user.id,
      acquiredClinicIds: agentAcquiredClinicIds,
      includeOwnOrders: agentIncludesOwnOrders,
    };
    // agentAcquiredClinicIdsKey collapses the array identity into a
    // string so the memo only fires when the contents actually change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAgentView, user?.id, agentAcquiredClinicIdsKey, agentIncludesOwnOrders]);

  const queryParams: orderQueries.OrderListParams = {
    // Multi-clinic podiatrists drop the single-clinic narrow so RLS
    // (clinic_id IN current_clinic_ids()) surfaces orders across every
    // membership. Without this, the tracker shows "0 matching" for orders
    // that live outside the user's defaultClinicId.
    clinicId: wideView || multiClinicPodiatrist ? null : clinicId,
    status,
    family,
    // Column filters still fetch a wider window and paginate client-side.
    // SEARCH does not: it is a server-side id filter now, so it keeps normal
    // pagination and an exact count.
    page: clientFilterMode ? 0 : page,
    pageSize: clientFilterMode ? 5000 : pageSize,
    sortBy,
    sortDir,
    agentScope,
    podiatristId: podiatristIdFilter,
    // Whole-dataset search (fn_search_order_ids). Agents match on order
    // number only: a name hit-count would leak that the name exists.
    search: searchTerm || undefined,
    searchIncludesNames: !isAgentView,
  };

  // R14 - admin queries don't depend on clinicId, so they don't need to
  // wait for profile resolution at all. Previously we gated on
  // `!profileLoading`, which meant if resolveProfileAndRole stalled (slow
  // PostgREST hop, browser-extension interference, etc.) the admin table
  // sat on "Loading..." forever even though the orders query is fully
  // independent. Podiatrist scope still waits on `!!clinicId` so the
  // empty-state copy doesn't false-positive during the resolution window.
  //
  // Bug-fix B: agent scope additionally waits on agentAcquiredClinicsQuery
  // so we don't fire a query with `acquiredClinicIds = []` before the
  // resolver lands (which would otherwise short-circuit to zero rows on
  // first paint).
  // Additive calendar view. Default is ALWAYS the list (the table is the
  // landing surface and the e2e contracts depend on it); the per-user
  // choice is remembered in localStorage following the hs-calendar-view
  // precedent (validated read, adopted once after auth lands, never
  // overriding an in-session switch). No server-side preference mechanism
  // exists beyond profiles columns, so localStorage is the house pattern.
  const [view, setView] = React.useState<'list' | 'calendar'>('list');
  const viewAdoptedRef = React.useRef(false);
  React.useEffect(() => {
    if (viewAdoptedRef.current || !user?.id) return;
    viewAdoptedRef.current = true;
    try {
      if (localStorage.getItem(`tracker-view:${user.id}`) === 'calendar') setView('calendar');
    } catch {
      // storage unavailable: stay on the list default
    }
  }, [user?.id]);
  const changeView = (v: 'list' | 'calendar'): void => {
    setView(v);
    if (user?.id) {
      try {
        localStorage.setItem(`tracker-view:${user.id}`, v);
      } catch {
        // best effort only
      }
    }
  };

  const query = useQuery({
    ...orderQueries.ordersListQuery(supabase, queryParams),
    enabled: isAgentView
      ? !!user?.id && !agentAcquiredClinicsQuery.isLoading
      : isPodiatristView
        // Podiatrist strict isolation: wait for user.id to land before
        // firing, otherwise we'd issue a query without the
        // podiatrist_id narrow and momentarily surface other
        // podiatrists' orders.
        ? !!user?.id && !!clinicId
        : wideView || !!clinicId,
    placeholderData: keepPreviousData,
  });

  // R37 - per-status counts feeding the seven filter boxes above the table.
  // Task B3: counts now respect the currently-selected family tab, so the
  // boxes reflect the same scope as the table rows below. Uses the same
  // { clinicId, wideView } gating as the list query so admin/agent see the
  // full set and podiatrists wait on clinic resolution.
  const statusCountsQuery = useQuery({
    ...orderQueries.ordersStatusCountsQuery(supabase, {
      // Mirror the list query: multi-clinic podiatrists count across every
      // membership (RLS still enforces clinic_id IN current_clinic_ids()).
      clinicId: wideView || multiClinicPodiatrist ? null : clinicId,
      family,
      // Bug-fix B: agent surface narrows by acquired clinics + own-orders gate.
      agentScope,
      // Podiatrist strict isolation: counts mirror the list query so the
      // status boxes reflect exactly the rows the viewer can see in the
      // table below.
      podiatristId: podiatristIdFilter,
    }),
    enabled: isAgentView
      ? !!user?.id && !agentAcquiredClinicsQuery.isLoading
      : isPodiatristView
        // Podiatrist strict isolation: mirror the list query gate so
        // the boxes don't briefly show counts from other podiatrists'
        // orders before user.id lands.
        ? !!user?.id && !!clinicId
        : wideView || !!clinicId,
    placeholderData: keepPreviousData,
  });

  // Calendar data: the SAME list query with ONLY paging widened (the
  // export/LPO precedent, calendarListParams is pure and test-pinned), so
  // the chips, family filter, search and RLS scope the calendar exactly as
  // they scope the table. Fires only while the calendar is on screen.
  const calendarQuery = useQuery({
    ...orderQueries.ordersListQuery(supabase, calendarListParams(queryParams)),
    enabled:
      view === 'calendar' &&
      !strandedMode &&
      (isAgentView
        ? !!user?.id && !agentAcquiredClinicsQuery.isLoading
        : isPodiatristView
          ? !!user?.id && !!clinicId
          : wideView || !!clinicId),
    placeholderData: keepPreviousData,
  });

  const statusCounts = React.useMemo(() => {
    const c = statusCountsQuery.data;
    const pending = c?.pending ?? 0;
    const submitted = c?.submitted ?? 0;
    const in_production = c?.in_production ?? 0;
    const in_delivery = c?.in_delivery ?? 0;
    const delivered = c?.delivered ?? 0;
    const active = pending + submitted + in_production + in_delivery;
    const total = active + delivered;
    // Indexed by FILTER_TABS key so the JSX is a simple lookup.
    return {
      active,
      total,
      pending,
      submitted,
      production: in_production,
      delivery: in_delivery,
      delivered,
    } as const;
  }, [statusCountsQuery.data]);

  // Stalled drafts (admin only). The count feeds the informational chip that
  // sits beside the status boxes; the list feeds the dedicated table when
  // the chip is active. Query keys live under ['orders', 'stranded', ...] so
  // every existing invalidateQueries({ queryKey: ['orders'] }) call reaches
  // them too. staleTime inherits the app default, same as the queries above.
  const isAdminView = effectiveScope === 'admin';
  const strandedCountQuery = useQuery({
    queryKey: orderQueries.ordersKeys.strandedCount(),
    queryFn: () => orderQueries.countStrandedDrafts(supabase),
    enabled: isAdminView,
    placeholderData: keepPreviousData,
  });
  const strandedCount = strandedCountQuery.data ?? 0;
  const strandedListQuery = useQuery({
    queryKey: orderQueries.ordersKeys.strandedList(),
    queryFn: () => orderQueries.listStrandedDrafts(supabase),
    enabled: strandedMode,
    placeholderData: keepPreviousData,
  });
  const strandedRows = strandedListQuery.data ?? [];

  // R28 - value extractors per filterable column. Used both to derive the
  // distinct option list shown in each popover and to evaluate the active
  // filter against each row.
  // Task B4/B5: patient + product extractors now match what the cell
  // actually renders so the popover options line up with what the user
  // sees in the table.
  const FILTER_EXTRACTORS: Record<string, (r: OrderListRow) => string | null> = {
    // 2026-08-10: `patient` is deliberately NOT here. The Patient column's
    // value checklist was removed (the header search matches patient names
    // across the whole dataset), and with no extractor a stale bookmarked
    // filter for it is ignored instead of silently narrowing the table.
    // W4: short_code extractor removed alongside the Patient ID column.
    status: (r) => r.status,
    podiatrist: (r) => r.podiatrist?.full_name ?? null,
    // Direct-billed orders surface "Direct Billing" as their clinic value
    // so the Clinic column filter popover lists it as a selectable option
    // alongside real clinic names, and selecting a real clinic excludes
    // direct-billed rows (since their extractor value never matches).
    clinic: (r) => (r.is_direct_billed ? DIRECT_BILLING_FILTER_VALUE : r.clinic?.name ?? null),
    product: (r) => formatProductLabel(r.primary_line),
    provider: (r) => routeLabel(r.primary_line?.production_path),
  };

  const allRows: OrderListRow[] = query.data?.rows ?? [];

  // Filter the rows against any active column filters (AND across columns)
  // and the mobile free-text search (patient name OR order number).
  const filteredRows = React.useMemo(() => {
    if (!clientFilterMode) return allRows;
    const entries = Object.entries(columnFilters);
    return allRows.filter((row) => {
      const columnsOk = entries.every(([col, values]) => {
        const extractor = FILTER_EXTRACTORS[col];
        if (!extractor) return true;
        const v = extractor(row);
        return v != null && values.includes(v);
      });
      if (!columnsOk) return false;
      // Search is applied by the SERVER now (queryParams.search), so every row
      // arriving here is already a match; only column filters run client-side.
      return true;
    });
    // FILTER_EXTRACTORS is a stable lookup table (keys + extractor fns are
    // static); re-created each render only because it's defined in-scope.
    // Adding it would invalidate the memo every render and defeat its purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRows, columnFilters, clientFilterMode, searchActive, searchTerm, isAgentView]);

  // Total + pagination flip to client-side numbers when filters/search are active.
  const effectiveTotal = clientFilterMode ? filteredRows.length : query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(effectiveTotal / pageSize));
  const displayedRows = clientFilterMode
    ? filteredRows.slice(page * pageSize, page * pageSize + pageSize)
    : filteredRows;

  // Distinct options per filterable column, computed from the loaded rows.
  // Non-chained: each column's options reflect the full loaded set (not
  // narrowed by other active filters). Chained options is a follow-up.
  const optionsFor = (col: string): ColumnFilterOption[] => {
    const extractor = FILTER_EXTRACTORS[col];
    if (!extractor) return [];
    const seen = new Set<string>();
    const out: ColumnFilterOption[] = [];
    for (const r of allRows) {
      const v = extractor(r);
      if (v == null || v === '') continue;
      if (seen.has(v)) continue;
      seen.add(v);
      out.push({ value: v, label: v });
    }
    return out.sort((a, b) => a.label.localeCompare(b.label));
  };

  // Status options are predefined enum values + labels (so the popover
  // shows every status, not only the ones present in the loaded rows).
  const statusOptions: ColumnFilterOption[] = STATUS_OPTIONS.map((s) => ({
    value: s,
    label: STATUS_LABEL[s] ?? s,
  }));

  const onSortDir = (col: orderQueries.OrderListParams['sortBy'], dir: 'asc' | 'desc') => {
    setParams({ sort: col ?? 'created_at', dir });
  };

  const onRowClick = (row: OrderListRow) => {
    // W3: agent view is a no-op. Agents cannot drill into order
    // detail (patient confidentiality + RLS denies most actions).
    // Early-return BEFORE navigate so the Order ID cell, etc., do
    // nothing on click. The Order ID cell also drops its
    // cursor-pointer / hover:underline so it does not visually
    // invite a click.
    if (effectiveScope === 'agent') return;
    // Admin lands on /admin/orders/:id (admin-scope detail page).
    // Podiatrists land on /orders/:id.
    navigate(effectiveScope === 'admin' ? `/admin/orders/${row.id}` : `/orders/${row.id}`);
  };

  // R14 - CSV export with column selector. Fetches up to 5000 rows for the
  // current filter (large enough to cover any realistic clinic) regardless
  // of the page-size selector. Excel opens UTF-8 CSV with BOM correctly,
  // so we don't need an XLSX dependency. Filename embeds the active filter.
  const ALL_COLUMNS: ReadonlyArray<{
    key: string;
    label: string;
    adminOnly?: boolean;
    /** Show the column for multi-clinic podiatrists too. Currently only
     *  Clinic, so they can tell which order belongs to which membership. */
    multiClinicPodiatrist?: boolean;
    extract: (row: OrderListRow) => string;
  }> = [
    { key: 'patient', label: 'Patient', extract: (r) => resolvePatientDisplay(r).name ?? '' },
    { key: 'order_number', label: 'Order ID', extract: (r) => r.order_number ?? '' },
    // W4: Patient ID column removed (the /patients page was hidden in
    // 983bca4, so primary_line.short_code has no remaining UI surface).
    { key: 'created_at', label: 'Date', extract: (r) => formatDate(r.created_at) },
    { key: 'podiatrist', label: 'Podiatrist', adminOnly: true, extract: (r) => r.podiatrist?.full_name ?? '' },
    {
      key: 'clinic',
      label: 'Clinic',
      adminOnly: true,
      multiClinicPodiatrist: true,
      extract: (r) => (r.is_direct_billed ? DIRECT_BILLING_FILTER_VALUE : r.clinic?.name ?? ''),
    },
    {
      key: 'product',
      label: 'Product',
      extract: (r) => formatProductLabel(r.primary_line),
    },
    {
      key: 'quantity',
      label: 'Qty',
      extract: (r) => {
        const total = lineQtyTotal(r);
        return total == null ? '' : String(total);
      },
    },
    {
      key: 'provider',
      label: 'Route',
      adminOnly: true,
      extract: (r) => routeLabel(r.primary_line?.production_path) ?? '',
    },
    { key: 'status', label: 'Status', extract: (r) => r.status },
    {
      key: 'expected_delivery',
      label: 'Expected delivery',
      extract: (r) => (r.expected_delivery_date ? formatDate(r.expected_delivery_date) : ''),
    },
  ];
  const exportableColumns = ALL_COLUMNS.filter(
    (c) => wideView || !c.adminOnly || (multiClinicPodiatrist && c.multiClinicPodiatrist),
  );

  const [exportOpen, setExportOpen] = React.useState(false);
  const [selectedColumns, setSelectedColumns] = React.useState<string[]>(() =>
    exportableColumns.map((c) => c.key),
  );
  const [exporting, setExporting] = React.useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const result = await orderQueries
        .ordersListQuery(supabase, { ...queryParams, page: 0, pageSize: 5000 })
        .queryFn();
      const cols = exportableColumns.filter((c) => selectedColumns.includes(c.key));
      const escape = (v: string) => {
        if (v.includes('"') || v.includes(',') || v.includes('\n')) {
          return `"${v.replace(/"/g, '""')}"`;
        }
        return v;
      };
      const header = cols.map((c) => escape(c.label)).join(',');
      const body = result.rows
        .map((r) => cols.map((c) => escape(c.extract(r))).join(','))
        .join('\n');
      // U+FEFF (BOM) prepended so Excel detects UTF-8 and renders accents
      // correctly. Written as an escape so ESLint's no-irregular-whitespace
      // rule doesn't trip on the literal BOM.
      const csv = `\uFEFF${header}\n${body}`;
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      a.download = `orders-${effectiveScope}-${status}-${ts}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      setExportOpen(false);
    } finally {
      setExporting(false);
    }
  };

  // R20+ withShell={false} skips the outer AppShell so this component
  // can be mounted inside another shell (e.g. the consolidated /agent
  // tabbed page). Default true keeps every legacy caller working.
  const Shell = withShell ? AppShell : React.Fragment;

  return (
    <Shell>
      <main className="px-6 py-5 max-w-[1400px] mx-auto">
        {/* R14 - tightened header. Eyebrow + title + count on one row,
            count moved alongside to free the vertical space the subtitle
            was eating. New-order button stays right. */}
        <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
          <div className="min-w-0">
            <Eyebrow>
              {effectiveScope === 'admin'
                ? 'Admin · Order tracker'
                : effectiveScope === 'agent'
                  ? 'Agent · Order tracker'
                  : t('orders:title_orders')}
            </Eyebrow>
            <div className="flex items-baseline gap-3 flex-wrap mt-1">
              <Heading as="h1" size="h3">
                {effectiveScope === 'admin'
                  ? 'All clinic orders'
                  : effectiveScope === 'agent'
                    ? 'Orders from your clinics'
                    : 'Your clinic orders'}
              </Heading>
              <span className="text-sm text-text-secondary">
                {strandedMode
                  ? strandedListQuery.data
                    ? `${strandedRows.length} stalled draft${strandedRows.length === 1 ? '' : 's'}`
                    : 'Loading…'
                  : query.data
                    ? clientFilterMode
                      ? `${effectiveTotal} matching · ${query.data.total} total`
                      : `${query.data.total} matching`
                    : 'Loading…'}
              </span>
            </div>
          </div>
          {/* R27 - Export moved up here next to + New order, matching
              the dashboard's right-cluster pattern. New order is
              size=lg so it matches the dashboard. */}
          <div className="flex items-center gap-2">
            {/* Export acts on the regular list query, which is hidden while
                the stalled-drafts table is up, so the button hides with it
                (there is no stalled-drafts CSV export by design). */}
            {/* LPO email generator (clipboard feature): sits immediately left of
                Export, same styling, and reuses THIS page's list query with
                THIS page's scope params, so it sees exactly what the page sees. */}
            {/* Additive list/calendar toggle: swaps ONLY the region below
                the status chips; chips, family filter and search stay put
                and drive both views identically. */}
            {!strandedMode ? (
              <div
                role="group"
                aria-label="View"
                className="inline-flex items-center gap-1 rounded-md border border-border-default p-0.5"
              >
                <Button
                  variant={view === 'list' ? 'primary' : 'ghost'}
                  size="sm"
                  aria-pressed={view === 'list'}
                  aria-label="List view"
                  onClick={() => changeView('list')}
                  data-testid="tracker-view-list"
                >
                  <List className="h-4 w-4" />
                </Button>
                <Button
                  variant={view === 'calendar' ? 'primary' : 'ghost'}
                  size="sm"
                  aria-pressed={view === 'calendar'}
                  aria-label="Calendar view"
                  onClick={() => changeView('calendar')}
                  data-testid="tracker-view-calendar"
                >
                  <CalendarDays className="h-4 w-4" />
                </Button>
              </div>
            ) : null}
            {/* Recycle bin: admin-only entry point, hidden in stranded mode
                like its neighbours. The page shipped with no way in but the
                URL; this is it. Renders null for non-admins itself. */}
            {!strandedMode ? <RecycleBinButton /> : null}
            {!strandedMode ? <LpoEmailButton listParams={queryParams} /> : null}
            {!strandedMode ? (
              <Button
                variant="outline"
                size="md"
                onClick={() => setExportOpen(true)}
                startIcon={<Download className="h-4 w-4" />}
              >
                Export
              </Button>
            ) : null}
            {canCreateOrders ? (
              <Button
                variant="primary"
                size="lg"
                onClick={() => navigate('/orders/new')}
                startIcon={<Plus className="h-4 w-4" />}
              >
                {t('orders:action_new_order')}
              </Button>
            ) : null}
          </div>
        </div>

        <Card>
          {/* R15 - collapsed the filter area into ONE row: family pills as a
              compact segmented switch on the left, status tabs in the
              middle, search + rows + export on the right. Wraps gracefully
              on narrow viewports. CardHeader padding shrunk to px-4 py-3
              (vs default px-6 py-5) to stop eating row real-estate. */}
          <CardHeader className="px-4 py-2">
            {/* Mobile (<sm) header zone - field-feedback redesign. The
                desktop row below wraps awkwardly on phones, so this
                dual-rendered compact zone replaces it under sm (the
                batch-4 pattern: same handlers + URL params, so both
                surfaces drive the same state; desktop DOM untouched):
                  1. family tabs as a full-width segmented control,
                  2. a persistent search bar (patient name / order number;
                     persistent rather than icon-reveal because the field
                     gap was DISCOVERABILITY - hiding search behind an icon
                     re-hides it, and the row costs ~40px),
                  3. the status counts as a swipeable strip of stat chips. */}
            <div className="sm:hidden flex flex-col gap-2" data-testid="tracker-mobile-header">
              {/* role=group + aria-pressed (not tablist/tab): these are
                  toggle buttons without arrow-key roving focus, matching the
                  status chip strip's semantics below. Hidden in
                  stalled-drafts mode: the family filter only shapes the
                  regular list query, which is not on screen there. */}
              {!strandedMode ? (
                <div
                  role="group"
                  aria-label="Order family"
                  className="flex rounded-lg border border-border-subtle bg-sand-50 p-0.5"
                >
                  {FAMILY_TABS.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      aria-pressed={family === tab.key}
                      data-testid={`m-family-tab-${tab.key}`}
                      onClick={() =>
                        setParams({ family: tab.key === 'all' ? null : tab.key, page: null })
                      }
                      className={[
                        'flex-1 min-w-0 truncate rounded-md px-2 py-2 text-xs font-medium transition-colors',
                        family === tab.key
                          ? 'bg-surface-elevated text-brand-700 shadow-sm'
                          : 'text-text-secondary',
                      ].join(' ')}
                    >
                      {t(tab.labelKey)}
                    </button>
                  ))}
                </div>
              ) : null}
              {!isAgentView ? (
                <div className="relative">
                  <Search
                    className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted pointer-events-none"
                    aria-hidden="true"
                  />
                  <input
                    type="search"
                    inputMode="search"
                    enterKeyHint="search"
                    value={searchTermRaw}
                    onChange={(e) => {
                      const v = e.target.value;
                      const patch: Record<string, string | null> = { q: v || null, page: null };
                      // Starting a search widens the scope to Total: the
                      // field gap this fixes is "find a patient's order",
                      // and that order is often already delivered, outside
                      // the default Active tab. The chip strip reflects the
                      // jump, and chips still narrow the search afterwards.
                      if (v.trim().length > 0 && !searchActive) patch.status = 'total';
                      setParams(patch);
                    }}
                    placeholder="Search patient or order number"
                    aria-label="Search orders by patient or order number"
                    data-testid="tracker-mobile-search"
                    // text-base = 16px so iOS never zooms on focus (house rule).
                    className="w-full h-10 rounded-md border border-border-default bg-surface-elevated ps-9 pe-9 text-base text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-brand-500 [&::-webkit-search-cancel-button]:hidden"
                  />
                  {searchActive ? (
                    <button
                      type="button"
                      aria-label="Clear search"
                      data-testid="tracker-mobile-search-clear"
                      onClick={() => setParams({ q: null, page: null })}
                      className="absolute end-0 top-0 h-10 w-10 flex items-center justify-center text-text-muted hover:text-text-primary"
                    >
                      <X className="h-4 w-4" aria-hidden="true" />
                    </button>
                  ) : null}
                </div>
              ) : null}
              {!isAgentView ? (
                <div
                  role="group"
                  aria-label="Filter orders by status"
                  className="flex flex-nowrap items-center overflow-x-auto gap-1.5 pb-1 -mx-1 px-1"
                >
                  {FILTER_TABS.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      aria-pressed={!strandedMode && status === tab.key}
                      data-testid={`m-status-chip-${tab.key}`}
                      onClick={() => setParams({ status: tab.key, page: null })}
                      className={[
                        'shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors',
                        !strandedMode && status === tab.key
                          ? 'bg-brand-700 border-brand-700 text-white font-semibold'
                          : 'bg-surface-elevated border-border-subtle text-text-secondary',
                      ].join(' ')}
                    >
                      <span className="font-semibold tabular-nums">
                        {statusCountsQuery.isLoading ? '-' : statusCounts[tab.key]}
                      </span>
                      {tab.label}
                    </button>
                  ))}
                  {/* Stalled-drafts chip (admin only, informational). Muted
                      on purpose: drafts can be legitimately parked. Rendered
                      while in the mode even at N=0 so it stays toggleable.
                      Entering also clears q/filters/page so a lingering
                      search or column filter cannot sit over the drafts
                      table. */}
                  {isAdminView && (strandedCount > 0 || strandedMode) ? (
                    <button
                      type="button"
                      aria-pressed={strandedMode}
                      data-testid="m-stranded-chip"
                      onClick={() =>
                        setParams(
                          strandedMode
                            ? { status: null, page: null }
                            : { status: 'stranded', q: null, filters: null, page: null },
                        )
                      }
                      className={[
                        'shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors',
                        strandedMode
                          ? 'bg-sand-100 border-border-default text-text-primary font-medium'
                          : 'bg-surface-elevated border-border-subtle text-text-muted',
                      ].join(' ')}
                    >
                      <span className="font-semibold tabular-nums">{strandedCount}</span>
                      Stalled drafts
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
            {/* Tracker-layout pass (2026-08-12): the desktop (>=sm) filter
                zone is now two side-by-side regions. LEFT: the seven status
                boxes on a SINGLE non-wrapping line (they shrink to their
                110px floor and then scroll horizontally instead of stacking
                into a second row). RIGHT: a fixed-width column stacking the
                family dropdown, the patient/order search, and the admin-only
                stalled-drafts chip top-to-bottom. Hidden below sm: the
                compact mobile zone above replaces it. */}
            <div className="flex items-start gap-3 max-sm:hidden">
              {/* Agent variant hides the status filter row so the surface
                  stays trimmed to the family dropdown + the table. The
                  `status` URL param still works; only the visual cards are
                  removed. */}
              {!isAgentView ? (
                <div
                  role="group"
                  aria-label="Filter orders by status"
                  className="flex flex-1 min-w-0 flex-nowrap items-stretch gap-2 overflow-x-auto pb-1"
                >
                  {FILTER_TABS.map((tab) => (
                    <StatusFilterCard
                      key={tab.key}
                      label={tab.label}
                      value={statusCounts[tab.key]}
                      accent={tab.accent}
                      selected={!strandedMode && status === tab.key}
                      loading={statusCountsQuery.isLoading}
                      onClick={() => setParams({ status: tab.key, page: null })}
                      data-testid={`status-box-${tab.key}`}
                    />
                  ))}
                </div>
              ) : null}
              {/* Right column. ms-auto keeps it pinned to the end when the
                  status row is hidden (agent view). */}
              <div className="ms-auto flex w-64 shrink-0 flex-col gap-2">
                {/* Family filter, converted from the segmented Tabs strip to
                    a dropdown. Same three options and the same URL-param
                    behaviour: 'all' is the default and nulls the param out so
                    the URL stays clean. Hides while the stalled-drafts table
                    is up because it only shapes the regular list query, which
                    is not on screen there. Native <select> is this page's
                    established dropdown pattern (rows-per-page, status pill,
                    provider pickers); there is no shadcn Select in
                    @insolech/ui. */}
                {!strandedMode ? (
                  <select
                    value={family}
                    onChange={(e) => {
                      const v = e.target.value;
                      setParams({ family: v === 'all' ? null : v, page: null });
                    }}
                    aria-label="Filter orders by product family"
                    data-testid="family-select"
                    className="h-8 w-full rounded-md border border-border-default bg-surface-elevated px-2 text-xs text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-500"
                  >
                    {FAMILY_TABS.map((tab) => (
                      <option key={tab.key} value={tab.key}>
                        {t(tab.labelKey)}
                      </option>
                    ))}
                  </select>
                ) : null}
                {/* Desktop search. Until 2026-08-10 the ONLY input lived in the
                    sm:hidden mobile header, so on a laptop there was no way to
                    search at all - which is how order 2600777 became unfindable.
                    The term now goes to the server (fn_search_order_ids), so it
                    matches the whole dataset from any page. */}
                <div className="relative">
                  <Search
                    className="pointer-events-none absolute start-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-muted"
                    aria-hidden="true"
                  />
                  <input
                    type="search"
                    value={searchTermRaw}
                    onChange={(e) => {
                      const v = e.target.value;
                      const patch: Record<string, string | null> = { q: v || null, page: null };
                      // A search spanning every status is what you want when
                      // hunting one order; mirrors the mobile input.
                      if (v.trim().length > 0 && !searchActive) patch.status = 'total';
                      setParams(patch);
                    }}
                    placeholder="Search patient or order number"
                    aria-label="Search orders by patient name or order number"
                    data-testid="tracker-search-desktop"
                    className="h-8 w-full rounded-full border border-border-subtle bg-surface ps-8 pe-3 text-xs text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
                {/* The dismiss chip stays for shared/bookmarked ?q= links. */}
                {searchActive ? (
                  <button
                    type="button"
                    onClick={() => setParams({ q: null, page: null })}
                    data-testid="tracker-search-chip"
                    aria-label={`Clear search "${searchTerm}"`}
                    className="self-start inline-flex items-center gap-1.5 rounded-full border border-border-subtle bg-sand-50 px-3 py-1.5 text-xs text-text-secondary hover:text-text-primary hover:border-border-default"
                  >
                    <Search className="h-3 w-3" aria-hidden="true" />
                    <span className="max-w-[160px] truncate">{`"${searchTerm}"`}</span>
                    <X className="h-3 w-3" aria-hidden="true" />
                  </button>
                ) : null}
                {/* Stalled-drafts chip (admin only). Informational, muted
                    pill below the search: drafts older than 24h with
                    zero status-history rows. Deliberately NOT a coloured
                    count box because parked drafts are often fine; it only
                    appears when there is something to look at (or while the
                    mode itself is active, so it stays toggleable at N=0).
                    Clicking toggles ?status=stranded, which swaps the table
                    below for the dedicated stalled-drafts view. Entering
                    also clears q/filters/page so a lingering search or
                    column filter cannot sit over the drafts table. */}
                {isAdminView && (strandedCount > 0 || strandedMode) ? (
                  <button
                    type="button"
                    aria-pressed={strandedMode}
                    data-testid="stranded-drafts-chip"
                    onClick={() =>
                      setParams(
                        strandedMode
                          ? { status: null, page: null }
                          : { status: 'stranded', q: null, filters: null, page: null },
                      )
                    }
                    className={[
                      'self-start inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors',
                      strandedMode
                        ? 'bg-sand-100 border-border-default text-text-primary font-medium'
                        : 'bg-sand-50 border-border-subtle text-text-secondary hover:text-text-primary hover:border-border-default',
                    ].join(' ')}
                  >
                    Stalled drafts:
                    <span className="font-semibold tabular-nums">{strandedCount}</span>
                  </button>
                ) : null}
              </div>
            </div>
          </CardHeader>
          <CardBody className="px-0 pb-0">
            {/* R15 - pushed the max-height onto the Table's own wrapper via
                wrapperClassName. Sticky thead now works because the table
                wrapper IS the scroller (previously a parent div was the
                scroller and the inner wrapper broke the sticky context). */}
            {/* R26 table layout per Rafael:
                  • Patient column is sticky-left so it stays visible
                    as you scroll horizontally
                  • Status column sits between Product and Order date
                    for quick scanning
                  • Inline editable status / provider / expected
                    delivery for admin (write directly via RLS)
                  • Actions column at the right with view / duplicate
                    (TODO) / delete. Non-admins see only View. */}
            {/* Stalled-drafts mode (admin only) swaps BOTH regular surfaces
                (mobile card list + desktop table) for one dedicated
                lightweight table fed by the admin_stranded_drafts view. The
                table renders at every width (it is narrow enough to scroll)
                so the mode works on mobile too. */}
            {strandedMode ? (
              <StrandedDraftsTable
                rows={strandedRows}
                loading={strandedListQuery.isLoading}
                error={strandedListQuery.isError ? strandedListQuery.error : null}
                onRowClick={(orderId) => navigate(`/admin/orders/${orderId}`)}
              />
            ) : view === 'calendar' ? (
              <OrdersCalendarView
                rows={calendarQuery.data?.rows ?? []}
                isLoading={calendarQuery.isLoading}
                isError={calendarQuery.isError}
                errorText={
                  calendarQuery.error instanceof Error ? calendarQuery.error.message : undefined
                }
                renderRowCard={(row) => (
                  <OrderRow
                    key={`cal-${row.id}`}
                    variant="card"
                    row={row}
                    wideView={wideView}
                    showClinicColumn={showClinicColumn}
                    isAgentView={isAgentView}
                    isAdmin={isAdmin}
                    canEditCells={canEditCells}
                    userClinics={userClinics}
                    currentUserId={user?.id ?? null}
                    onView={() => onRowClick(row)}
                  />
                )}
              />
            ) : (
              <>
            {/* Batch 4 — mobile card list (<sm only). Maps the SAME
                displayedRows the table maps below, so the status strip,
                column filters, search, and pagination all apply identically
                (single source of truth). Loading/empty/error states mirror
                the table's, styled for cards. */}
            <div className="sm:hidden flex flex-col gap-2" data-testid="order-card-list">
              {profileLoading || query.isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={`csk-${i}`} className="h-[74px] w-full rounded-lg" />
                ))
              ) : query.isError ? (
                <div className="text-error-700 text-sm py-4 space-y-1">
                  <div>Could not load orders.</div>
                  <div className="font-mono text-xs whitespace-pre-wrap break-all">
                    {query.error instanceof Error
                      ? query.error.message
                      : String((query.error as { message?: string })?.message ?? query.error)}
                  </div>
                </div>
              ) : displayedRows.length === 0 ? (
                <EmptyState
                  icon={<ClipboardList className="h-6 w-6" />}
                  title={t('orders:empty_no_results_title')}
                  description={
                    searchActive
                      ? status !== 'total'
                        ? `No orders match "${searchTerm}" in this status view. Select Total to search all orders.`
                        : `No orders match "${searchTerm}". Check the spelling or try an order number.`
                      : filtersActive
                        ? 'No orders match the active column filters. Try clearing one of them.'
                        : t('orders:empty_no_results_body')
                  }
                />
              ) : (
                displayedRows.map((row) => (
                  <OrderRow
                    key={`card-${row.id}`}
                    variant="card"
                    row={row}
                    wideView={wideView}
                    showClinicColumn={showClinicColumn}
                    isAgentView={isAgentView}
                    isAdmin={isAdmin}
                    canEditCells={canEditCells}
                    userClinics={userClinics}
                    currentUserId={user?.id ?? null}
                    onView={() => onRowClick(row)}
                  />
                ))
              )}
            </div>
            {/* Mobile: table hidden below sm (the card list above replaces
                it); the max-sm:max-h-none keeps the batch-2 single-scroller
                behavior for any state where the table is visible. Desktop
                unchanged (max-sm classes are inert >=sm). */}
            <Table wrapperClassName="max-h-[calc(100vh-220px)] max-sm:max-h-none max-sm:hidden overflow-auto">
              <TableHeader className="sticky top-0 z-20 bg-sand-50 shadow-[0_1px_0_0_var(--color-border-subtle)]">
                <TableRow>
                  {/* R28 - each header is a ColumnFilterPopover: click opens
                      sort A→Z / Z→A + search + multi-select checklist.
                      Filters auto-apply; sort fires onSortDir + closes. */}
                  {/* Patient column is hidden in agent view for patient
                      confidentiality. Order ID becomes the leading column. */}
                  {!isAgentView ? (
                    <TableHead className="sticky start-0 z-30 bg-sand-50 max-sm:max-w-[124px] max-sm:truncate">
                      <ColumnFilterPopover
                        label="Patient"
                        sortActive={sortBy === 'patient'}
                        sortDirection={sortDir}
                        onSortAsc={() => onSortDir('patient', 'asc')}
                        onSortDesc={() => onSortDir('patient', 'desc')}
                        sortOnlyHint={<span>{USE_PAGE_SEARCH_NOTE}</span>}
                      />
                    </TableHead>
                  ) : null}
                  <TableHead
                    className={
                      isAgentView ? 'sticky start-0 z-30 bg-sand-50' : undefined
                    }
                  >
                    <ColumnFilterPopover
                      label="Order ID"
                      sortActive={sortBy === 'order_number'}
                      sortDirection={sortDir}
                      onSortAsc={() => onSortDir('order_number', 'asc')}
                      onSortDesc={() => onSortDir('order_number', 'desc')}
                      sortOnlyHint={<span>{USE_PAGE_SEARCH_NOTE}</span>}
                    />
                  </TableHead>
                  {/* W4: Patient ID header removed. The /patients page
                      was hidden in 983bca4, so the column has no further
                      use. Order ID remains as the single identifier. */}
                  {/* Task B1: Product moved to slot #3 (between Order ID and
                      Status). Sits right after Order ID for the agent view.
                      The duplicate Product header that used to live after the
                      Clinic column is removed below. */}
                  <TableHead>
                    <ColumnFilterPopover
                      label="Product"
                      sortActive={false}
                      sortDirection={sortDir}
                      onSortAsc={() => onSortDir('order_number', 'asc')}
                      onSortDesc={() => onSortDir('order_number', 'desc')}
                      options={optionsFor('product')}
                      selected={columnFilters.product ?? []}
                      onSelectedChange={(next) => setColumnFilter('product', next)}
                      scopeNote={LOADED_VALUES_NOTE}
                      onGlobalSearch={handoffToSearch}
                    />
                  </TableHead>
                  {/* Qty (pairs) - narrow, content-driven column right after
                      Product. No filter/sort popover: 1-2 digit content, and
                      quantity lives on order_lines so a server sort would
                      need a foreignTable order (see Product's fallback). */}
                  <TableHead className="text-center">Qty</TableHead>
                  <TableHead>
                    <ColumnFilterPopover
                      label="Status"
                      sortActive={sortBy === 'status'}
                      sortDirection={sortDir}
                      onSortAsc={() => onSortDir('status', 'asc')}
                      onSortDesc={() => onSortDir('status', 'desc')}
                      options={statusOptions}
                      selected={columnFilters.status ?? []}
                      onSelectedChange={(next) => setColumnFilter('status', next)}
                      scopeNote={ALL_STATUSES_NOTE}
                      onGlobalSearch={handoffToSearch}
                    />
                  </TableHead>
                  {/* max-md: Order date + Expected delivery hidden (<768px)
                      to cut the horizontal scroll distance; both dates stay
                      one tap away on the order detail. Cells hidden in
                      matching pairs below. Tablets >=768px see the full set. */}
                  <TableHead className="max-md:hidden">
                    <ColumnFilterPopover
                      label="Order date"
                      kind="date"
                      sortActive={sortBy === 'created_at'}
                      sortDirection={sortDir}
                      onSortAsc={() => onSortDir('created_at', 'asc')}
                      onSortDesc={() => onSortDir('created_at', 'desc')}
                    />
                  </TableHead>
                  {/* R27 - Expected delivery moved between Order date
                      and Podiatrist for a tighter scan of the timeline
                      (placed + due) before clinic/product columns.
                      Hidden in agent view. */}
                  {!isAgentView ? (
                    <TableHead className="max-md:hidden">
                      <ColumnFilterPopover
                        label="Expected delivery"
                        kind="date"
                        sortActive={sortBy === 'expected_delivery_date'}
                        sortDirection={sortDir}
                        onSortAsc={() => onSortDir('expected_delivery_date', 'asc')}
                        onSortDesc={() => onSortDir('expected_delivery_date', 'desc')}
                      />
                    </TableHead>
                  ) : null}
                  {/* Podiatrist column stays on wideView (admin + agent only);
                      multi-clinic podiatrists don't need it because it is
                      always themselves. */}
                  {wideView ? (
                    <TableHead>
                      <ColumnFilterPopover
                        label="Podiatrist"
                        sortActive={false}
                        sortDirection={sortDir}
                        onSortAsc={() => onSortDir('order_number', 'asc')}
                        onSortDesc={() => onSortDir('order_number', 'desc')}
                        options={optionsFor('podiatrist')}
                        selected={columnFilters.podiatrist ?? []}
                        onSelectedChange={(next) => setColumnFilter('podiatrist', next)}
                        scopeNote={LOADED_VALUES_NOTE}
                        onGlobalSearch={handoffToSearch}
                      />
                    </TableHead>
                  ) : null}
                  {/* Clinic column shows for admin + agent AND for multi-clinic
                      podiatrists, so they can tell which order belongs to
                      which clinic. */}
                  {showClinicColumn ? (
                    <TableHead>
                      <ColumnFilterPopover
                        label="Clinic"
                        sortActive={false}
                        sortDirection={sortDir}
                        onSortAsc={() => onSortDir('order_number', 'asc')}
                        onSortDesc={() => onSortDir('order_number', 'desc')}
                        options={optionsFor('clinic')}
                        selected={columnFilters.clinic ?? []}
                        onSelectedChange={(next) => setColumnFilter('clinic', next)}
                        scopeNote={LOADED_VALUES_NOTE}
                        onGlobalSearch={handoffToSearch}
                      />
                    </TableHead>
                  ) : null}
                  {/* Route column (provider routing) hidden in agent view. */}
                  {wideView && !isAgentView ? (
                    <TableHead>
                      <ColumnFilterPopover
                        label="Route"
                        sortActive={false}
                        sortDirection={sortDir}
                        onSortAsc={() => onSortDir('order_number', 'asc')}
                        onSortDesc={() => onSortDir('order_number', 'desc')}
                        options={optionsFor('provider')}
                        selected={columnFilters.provider ?? []}
                        onSelectedChange={(next) => setColumnFilter('provider', next)}
                        scopeNote={LOADED_VALUES_NOTE}
                        onGlobalSearch={handoffToSearch}
                      />
                    </TableHead>
                  ) : null}
                  {/* Actions cluster (View / Duplicate / Delete) hidden in
                      agent view. The row remains clickable via the Order
                      ID cell to navigate to the detail page. */}
                  {!isAgentView ? (
                    <TableHead className="text-end">Actions</TableHead>
                  ) : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {profileLoading || query.isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={`sk-${i}`}>
                      <TableCell colSpan={totalColumns}>
                        <Skeleton className="h-6 w-full" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : query.isError ? (
                  <TableRow>
                    <TableCell colSpan={totalColumns}>
                      <div className="text-error-700 text-sm py-4 space-y-1">
                        <div>Could not load orders.</div>
                        <div className="font-mono text-xs whitespace-pre-wrap break-all">
                          {(() => {
                            const e = query.error as unknown;
                            if (e && typeof e === 'object') {
                              const err = e as {
                                message?: string;
                                details?: string;
                                hint?: string;
                                code?: string;
                              };
                              const parts: string[] = [];
                              if (err.code) parts.push(`code=${err.code}`);
                              if (err.message) parts.push(err.message);
                              if (err.details) parts.push(`details: ${err.details}`);
                              if (err.hint) parts.push(`hint: ${err.hint}`);
                              if (parts.length > 0) return parts.join(' | ');
                              try {
                                return JSON.stringify(err);
                              } catch {
                                return String(err);
                              }
                            }
                            return String(e);
                          })()}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : displayedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={totalColumns}>
                      <EmptyState
                        icon={<ClipboardList className="h-6 w-6" />}
                        title={t('orders:empty_no_results_title')}
                        description={
                          searchActive
                            ? status !== 'total'
                              ? `No orders match "${searchTerm}" in this status view. Select Total to search all orders.`
                              : `No orders match "${searchTerm}". Check the spelling or try an order number.`
                            : filtersActive
                              ? 'No orders match the active column filters. Try clearing one of them.'
                              : t('orders:empty_no_results_body')
                        }
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedRows.map((row) => (
                    <OrderRow
                      key={row.id}
                      row={row}
                      wideView={wideView}
                      showClinicColumn={showClinicColumn}
                      isAgentView={isAgentView}
                      isAdmin={isAdmin}
                      canEditCells={canEditCells}
                      userClinics={userClinics}
                      currentUserId={user?.id ?? null}
                      onView={() => onRowClick(row)}
                    />
                  ))
                )}
              </TableBody>
            </Table>
              </>
            )}
          </CardBody>
          {/* R27 - footer always rendered (even with one page) so the
              rows-per-page selector has a stable home. Pagination
              buttons hide when not needed. Stalled-drafts mode swaps it
              for a plain total line (no pagination, small fixed set). */}
          {strandedMode ? (
            <div className="flex items-center justify-between border-t border-border-subtle px-4 py-3 text-sm">
              <span className="text-text-muted">
                {strandedRows.length} stalled draft{strandedRows.length === 1 ? '' : 's'}
              </span>
            </div>
          ) : view === 'calendar' ? null : (
          <div className="flex items-center justify-between border-t border-border-subtle px-4 py-3 text-sm max-sm:flex-wrap max-sm:gap-y-2">
            <label className="flex items-center gap-2 text-text-secondary">
              <span>Rows per page</span>
              <select
                value={pageSize}
                aria-label="Rows per page"
                onChange={(e) => setParams({ size: e.target.value, page: null })}
                className="h-8 rounded-md border border-border-default bg-surface-elevated px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            {effectiveTotal > pageSize ? (
              <div className="flex items-center gap-3">
                <span className="text-text-muted">
                  Page {page + 1} of {totalPages}
                </span>
                <button
                  type="button"
                  className="px-3 py-1 rounded-md border border-border-subtle disabled:opacity-50"
                  disabled={page === 0}
                  onClick={() => setParam('page', String(Math.max(0, page - 1)))}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="px-3 py-1 rounded-md border border-border-subtle disabled:opacity-50"
                  disabled={page + 1 >= totalPages}
                  onClick={() => setParam('page', String(page + 1))}
                >
                  Next
                </button>
              </div>
            ) : (
              <span className="text-text-muted">
                {effectiveTotal} total
                {filtersActive && (query.data?.total ?? 0) !== effectiveTotal
                  ? ` (of ${query.data?.total ?? 0})`
                  : ''}
              </span>
            )}
          </div>
          )}
        </Card>

        {!strandedMode && !query.isLoading && (query.data?.total ?? 0) === 0 && status === 'active' ? (
          <Card className="mt-6 bg-sand-50">
            <CardHeader>
              <CardTitle>Welcome to your order tracker</CardTitle>
              <CardDescription>
                When orders move through production, delivery, and pickup, they show up here.
                Use the tabs above to switch between active and historic views.
              </CardDescription>
            </CardHeader>
            <CardBody />
          </Card>
        ) : null}

        {/* R14 - Export-to-CSV column picker. Modal lives at the bottom of
            the page rather than inline near the trigger so the tab/filter
            UI doesn't shift when it opens. */}
        <Modal open={exportOpen} onOpenChange={setExportOpen}>
          <ModalContent size="sm">
            <ModalHeader>
              <ModalTitle>Export orders</ModalTitle>
              <ModalDescription>
                {query.data?.total
                  ? `Pick the columns you want, then download the ${query.data.total} order${
                      query.data.total === 1 ? '' : 's'
                    } in the current filter as a CSV file. Excel opens this file directly.`
                  : 'Pick the columns you want and download as CSV.'}
              </ModalDescription>
            </ModalHeader>
            <div className="px-6 pb-4 flex flex-col gap-2 max-h-[60vh] overflow-y-auto">
              {exportableColumns.map((c) => (
                <label
                  key={c.key}
                  className="flex items-center gap-2 text-sm text-text-primary cursor-pointer select-none py-1"
                >
                  <input
                    type="checkbox"
                    checked={selectedColumns.includes(c.key)}
                    onChange={(e) => {
                      setSelectedColumns((prev) =>
                        e.target.checked
                          ? [...prev, c.key]
                          : prev.filter((k) => k !== c.key),
                      );
                    }}
                    className="h-4 w-4 rounded border-border-default text-brand-600 focus:ring-brand-500 focus:ring-offset-0"
                  />
                  {c.label}
                </label>
              ))}
            </div>
            <div className="px-6 pb-6 pt-2 flex items-center justify-end gap-2 border-t border-border-subtle">
              <Button
                variant="outline"
                size="md"
                onClick={() => setExportOpen(false)}
                disabled={exporting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleExport}
                disabled={selectedColumns.length === 0 || exporting}
                startIcon={<Download className="h-4 w-4" />}
              >
                {exporting ? 'Preparing…' : 'Download CSV'}
              </Button>
            </div>
          </ModalContent>
        </Modal>
      </main>
    </Shell>
  );
}

function formatDate(s: string): string {
  try {
    return new Date(s).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return s;
  }
}

/* -------------------------------------------------------------------------- */
/*  Task B6: agent status display (Confirmed / Not confirmed)                 */
/* -------------------------------------------------------------------------- */

interface AgentStatusDisplay {
  label: 'Confirmed' | 'Not confirmed';
  className: string;
}

const AGENT_CONFIRMED_STATUSES = new Set<string>([
  'in_production',
  'in_delivery',
  'delivered',
]);

function agentStatusDisplay(status: string): AgentStatusDisplay {
  if (AGENT_CONFIRMED_STATUSES.has(status)) {
    return {
      label: 'Confirmed',
      className: 'bg-success-50 text-success-700 border-success-200',
    };
  }
  return {
    label: 'Not confirmed',
    className: 'bg-sand-100 text-text-secondary border-border-subtle',
  };
}


/**
 * OrderRow - single row in the order tracker. Splits out the row body
 * so each row can have its own state for inline edits (status, date,
 * provider) without re-rendering siblings.
 *
 * Inline edit semantics (admin-only; non-admins see read-only):
 *   - Status: native <select> styled as a colored badge. Saves on change.
 *   - Expected delivery: native <input type="date">. Saves on change.
 *   - Provider: native <select> with all active providers + "To be
 *     decided". Writes to order_lines.assigned_provider_id on the
 *     primary line. Saves on change.
 *
 * All writes are direct supabase updates; RLS allows admin to modify
 * orders + order_lines. On success we invalidate the orders list query
 * so the row re-renders with the latest values.
 */
export function OrderRow({
  row,
  wideView,
  showClinicColumn,
  isAgentView,
  isAdmin,
  canEditCells,
  userClinics,
  currentUserId,
  onView,
  variant = 'row',
}: {
  row: OrderListRow;
  wideView: boolean;
  /** True when the Clinic column should render. Mirrors the header-level
   *  gate (wideView || multiClinicPodiatrist) so multi-clinic podiatrists
   *  can tell which order belongs to which clinic. */
  showClinicColumn: boolean;
  /** Agent surface trim: hides Patient, Expected delivery, Route, and
   *  the Actions cluster. (The Patient ID column was dropped for every
   *  role in W4.) */
  isAgentView: boolean;
  isAdmin: boolean;
  /** Master gate: when false, every inline editor in this row renders
   *  as a plain text label (no dropdown, no date picker, no clickable
   *  pill). Used to lock down the agent view end-to-end while leaving
   *  admin + podiatrist behaviour untouched. */
  canEditCells: boolean;
  /** Clinics the current user belongs to — drives the inline clinic
   *  re-assign dropdown for non-admin podiatrists. Admins see ALL clinics
   *  via a separate lazy query. */
  userClinics: ReadonlyArray<{ id: string; name: string }>;
  /** Auth uid of the viewer. Drives the row-level gate for the inline
   *  status dropdown: podiatrists can flip their own orders between
   *  draft and submitted, while admins keep the full enum and everyone
   *  else (including non-author clinic members) sees a read-only badge. */
  currentUserId: string | null;
  onView: () => void;
  /** Batch 4 — 'row' renders the classic <tr> for the desktop table; 'card'
   *  renders the mobile card (patient / order no / product / status badge +
   *  a three-dot menu holding the SAME actions, driven by the SAME
   *  mutations and eligibility rules below). Default 'row'. */
  variant?: 'row' | 'card';
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [exportingExcel, setExportingExcel] = React.useState(false);
  const [creatingDraft, setCreatingDraft] = React.useState(false);
  const [downloadingPdf, setDownloadingPdf] = React.useState(false);
  // Card variant only: controlled menu so async actions can hold it open
  // to show their busy label, then close themselves (see the card render).
  const [cardMenuOpen, setCardMenuOpen] = React.useState(false);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: orderQueries.ordersKeys.all });
  };

  // R35 — inline clinic re-assign. Admin can move an order to any clinic
  // (full list via lazy RPC); non-admin members can move it among the
  // clinics they belong to. RLS enforces both boundaries server-side.
  const clinicMut = useMutation({
    mutationFn: async (nextClinicId: string) => {
      const { error } = await supabase
        .from('orders')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .update({ clinic_id: nextClinicId } as any)
        .eq('id', row.id);
      if (error) throw error;
    },
    onSuccess: invalidate,
    onError: (e) =>
      alert(`Could not change clinic: ${e instanceof Error ? e.message : String(e)}`),
  });

  // Inline podiatrist re-assign (admin-only). Goes through the
  // reassign_order_podiatrist RPC instead of a direct orders.podiatrist_id
  // UPDATE: the RPC checks the target is an ACTIVE member of the ORDER's
  // clinic holding the podiatrist role (23514 otherwise), writes the
  // 'order.podiatrist_reassigned' audit row, and is a no-op for the current
  // holder. A failure is shown inline under the select rather than as an
  // alert, so the admin keeps the row in view while reading it.
  const [podiatristError, setPodiatristError] = React.useState<string | null>(null);
  const podiatristMut = useMutation({
    mutationFn: (nextPodiatristId: string) =>
      orderMutations.reassignOrderPodiatrist(supabase, {
        orderId: row.id,
        podiatristId: nextPodiatristId,
      }),
    onMutate: () => setPodiatristError(null),
    onSuccess: invalidate,
    onError: (e) => setPodiatristError(podiatristReassignMessage(e)),
  });

  const statusMut = useMutation({
    mutationFn: async (next: string) => {
      // Item 7: an INCOMPLETE draft cannot be advanced out of draft into the
      // pipeline (submitted, in_production, etc.) from the tracker by anyone -
      // only draft->cancelled is allowed. The DB gate (guard_draft_completeness
      // + the set_podiatrist_order_status raise) enforces this authoritatively;
      // this client short-circuit gives an immediate, clear message without a
      // round-trip. Only an explicit `false` blocks (null = legacy/ungated).
      if (
        row.status === 'draft' &&
        next !== 'draft' &&
        next !== 'cancelled' &&
        row.draft_complete === false
      ) {
        throw new Error(
          'This draft is incomplete and cannot be submitted from the tracker. Open it in the order wizard, complete the required prescription fields, then submit from there.',
        );
      }
      // Podiatrists flip their own pending orders between {draft, submitted}
      // via the SECURITY DEFINER set_podiatrist_order_status RPC. The RPC
      // exists because:
      //   1) orders.status is a DERIVED field. The recompute_order_status
      //      trigger re-derives it from order_lines.status on every line
      //      edit, so any direct write to orders.status is non-durable.
      //   2) Direct UPDATEs on orders for podiatrists are silently filtered
      //      by RLS for direct-billed orders the podiatrist owns but whose
      //      clinic she is not a member of (see migration
      //      20261115000000_set_podiatrist_order_status_rpc.sql for the
      //      full root-cause writeup). PostgREST returns 204 with zero rows
      //      and no error, so the previous code silently no-opped.
      // The RPC flips every line; the cascade trigger then rebuilds the
      // header status. Admins keep the direct-update path because they need
      // the full enum (in_production, in_delivery, delivered) which the
      // line-cascade path cannot express in one call. To make sure RLS
      // never silently filters their write either, we request the row back
      // and treat a zero-row response as an explicit failure.
      if (!isAdmin) {
        if (next !== 'draft' && next !== 'submitted') {
          throw new Error(`Podiatrists cannot move status to ${next}`);
        }
        const { error } = await supabase.rpc(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          'set_podiatrist_order_status' as any,
          { p_order_id: row.id, p_status: next } as unknown as Record<string, unknown>,
        );
        if (error) throw error;
        return;
      }
      const { data, error } = await supabase
        .from('orders')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .update({ status: next } as any)
        .eq('id', row.id)
        .select('id');
      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error(
          'Status update returned zero rows. RLS may have blocked the action; check the order is still mutable.',
        );
      }
    },
    onSuccess: invalidate,
    onError: (e) => {
      // Supabase returns PostgrestError objects (not Error instances) whose
      // useful info lives on `message` / `code` / `details` / `hint`. The
      // previous `String(e)` fallback produced "[object Object]" and hid the
      // real reason, e.g. the direct-billing payment gate raised by
      // enforce_direct_billing_payment_gate. Mirrors the deleteMut handler
      // below so admins see every field we can surface.
      const parts: string[] = [];
      if (e instanceof Error) {
        parts.push(e.message);
      } else if (e && typeof e === 'object') {
        const pe = e as { message?: string; code?: string; details?: string; hint?: string };
        if (pe.message) parts.push(pe.message);
        if (pe.code) parts.push(`code: ${pe.code}`);
        if (pe.details) parts.push(`details: ${pe.details}`);
        if (pe.hint) parts.push(`hint: ${pe.hint}`);
      } else {
        parts.push(String(e));
      }
      const msg = parts.length > 0 ? parts.join('\n') : 'Unknown error';
      console.error('[status update]', e);
      alert(`Could not update status:\n${msg}`);
    },
  });

  // Warn before re-dating an order a podiatrist has already been promised: a
  // change made after that announcement's 21:00 correction window reaches
  // nobody by email, so the tracker says so at the moment of the edit.
  const announcedGuard = useAnnouncedDateGuard();

  const dateMut = useMutation({
    mutationFn: async (next: string | null) => {
      const { error } = await supabase
        .from('orders')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .update({ expected_delivery_date: next } as any)
        .eq('id', row.id);
      if (error) throw error;
    },
    onSuccess: invalidate,
    onError: (e) =>
      alert(`Could not update delivery date: ${e instanceof Error ? e.message : String(e)}`),
  });

  // Urgent flag: a VISUAL marker only. Direct update under the EXISTING
  // update policy (clinic member on own clinic's non-terminal orders,
  // admin anywhere) and the untouched blacklist column guard; .select()
  // exposes the silent 0-row RLS filter so the user hears why nothing
  // changed instead of the flag quietly not sticking.
  const urgentMut = useMutation({
    mutationFn: async (next: boolean) => {
      const { data, error } = await supabase
        .from('orders')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .update({ is_urgent: next } as any)
        .eq('id', row.id)
        .select('id');
      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error(
          'delivered, cancelled or returned orders can only be flagged by an INSOLECH admin',
        );
      }
    },
    onSuccess: invalidate,
    onError: (e) =>
      alert(`Could not update the urgent flag: ${e instanceof Error ? e.message : String(e)}`),
  });

  // R27 - admin can correct the order's created_at (rare, but useful
  // when an order is back-dated to match a clinic's paper records).
  const orderDateMut = useMutation({
    mutationFn: async (next: string | null) => {
      if (!next) throw new Error('Order date is required');
      const { error } = await supabase
        .from('orders')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .update({ created_at: next } as any)
        .eq('id', row.id);
      if (error) throw error;
    },
    onSuccess: invalidate,
    onError: (e) =>
      alert(`Could not update order date: ${e instanceof Error ? e.message : String(e)}`),
  });

  const [routeModalOpen, setRouteModalOpen] = React.useState(false);

  // Task C: duplicate-order. Calls the duplicate_order RPC (see
  // packages/db/supabase/migrations/20261114000100_duplicate_order_rpc.sql)
  // which clones the order header + every line into a fresh DRAFT owned by
  // the caller. RPC enforces admin-or-original-podiatrist auth. On success
  // we invalidate the orders list (so the new draft appears in the tracker)
  // and navigate to the detail page in the scope-appropriate URL space.
  const duplicateMut = useMutation({
    mutationFn: () => orderMutations.duplicateOrder(supabase, row.id),
    onSuccess: (newId) => {
      invalidate();
      navigate(isAdmin ? `/admin/orders/${newId}` : `/orders/${newId}`);
    },
    onError: (e) => {
      const parts: string[] = [];
      if (e instanceof Error) {
        parts.push(e.message);
      } else if (e && typeof e === 'object') {
        const pe = e as { message?: string; code?: string; details?: string; hint?: string };
        if (pe.message) parts.push(pe.message);
        if (pe.code) parts.push(`code: ${pe.code}`);
        if (pe.details) parts.push(`details: ${pe.details}`);
        if (pe.hint) parts.push(`hint: ${pe.hint}`);
      } else {
        parts.push(String(e));
      }
      const msg = parts.length > 0 ? parts.join('\n') : 'Unknown error';
      console.error('[duplicate order]', e);
      alert(`Could not duplicate order:\n${msg}`);
    },
  });

  const deleteMut = useMutation({
    mutationFn: async () => {
      // Soft delete: the order moves to the admin recycle bin for 60 days
      // instead of being destroyed.
      //
      // Authorization is UNCHANGED. The RPC asks can_delete_order(), which
      // restates the same predicate the two existing DELETE policies carry,
      // so exactly who may remove an order is what it was before. It also
      // removes the old zero-rows ambiguity: a refusal now raises 42501
      // with a reason instead of looking identical to success.
      const { error } = await supabase.rpc('soft_delete_order' as never, {
        p_order_id: row.id,
      } as never);
      if (error) throw error;
    },
    onSuccess: invalidate,
    onError: (e) => {
      // Supabase returns PostgrestError objects (not Error instances) whose
      // useful info lives on `message` / `code` / `details` / `hint`.
      // The previous `String(e)` fallback produced "[object Object]" and
      // hid the real reason — surface every field we can.
      const pe =
        e && typeof e === 'object'
          ? (e as { message?: string; code?: string; details?: string; hint?: string })
          : null;
      const message = e instanceof Error ? e.message : pe?.message;
      console.error('[delete order]', e);

      // The BEFORE DELETE guard (prevent_referenced_order_delete) raises a
      // self-explanatory sentence with SQLSTATE P0001. Show it verbatim, with no
      // "code:/details:" noise, so the admin sees exactly what still references
      // the order and what to do instead.
      if (message && (pe?.code === 'P0001' || /^Cannot delete order/.test(message))) {
        alert(message);
        return;
      }

      const parts: string[] = [];
      if (e instanceof Error) {
        parts.push(e.message);
      } else if (pe) {
        if (pe.message) parts.push(pe.message);
        if (pe.code) parts.push(`code: ${pe.code}`);
        if (pe.details) parts.push(`details: ${pe.details}`);
        if (pe.hint) parts.push(`hint: ${pe.hint}`);
      } else {
        parts.push(String(e));
      }
      const msg = parts.length > 0 ? parts.join('\n') : 'Unknown error';
      alert(`Could not delete order:\n${msg}`);
    },
  });

  /* ------------------------------------------------------------------ */
  /* Shared action handlers + eligibility (Batch 4).                     */
  /* Used by BOTH the table row's icon cluster and the mobile card's     */
  /* three-dot menu, so the two surfaces can never drift.                */
  /* ------------------------------------------------------------------ */

  // Eligibility (identical rules to the pre-Batch-4 icon cluster):
  const canMailDraft =
    isAdmin &&
    !!row.primary_line?.production_path &&
    (row.primary_line.production_path === 'local' ||
      !!row.primary_line.assigned_provider?.id);
  const canWorkshopPdf =
    isAdmin &&
    (row.primary_line?.line_type === 'custom_insole' ||
      row.primary_line?.line_type === 'premium_3d_insole');
  const isPendingForPodiatrist = row.status === 'draft' || row.status === 'submitted';
  const deleteDisabledByStatus = !isAdmin && !isPendingForPodiatrist;

  const handleDuplicate = () => {
    if (duplicateMut.isPending) return;
    if (
      confirm(
        `Duplicate order ${row.order_number ?? ''} as a new draft?\n\nThe new order is owned by you and starts in Draft. Files are not copied.`,
      )
    ) {
      duplicateMut.mutate();
    }
  };

  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      // Lazy-fetch the full order detail — the list query returns only the
      // row summary, which doesn't carry configuration.deep.
      const detail = await orderQueries.orderDetailQuery(supabase, row.id).queryFn();
      if (!detail) {
        alert('Order not found.');
        return;
      }
      await exportOrderToExcel(detail);
    } catch (e) {
      alert(`Excel export failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setExportingExcel(false);
    }
  };

  const handleCreateSupplierDraft = async () => {
    const line = row.primary_line;
    const route = line?.production_path;
    if (!line || !route) return;
    setCreatingDraft(true);
    try {
      // Lazy-fetch the full detail — the list row lacks configuration.deep
      // (needed for the sandal vars + Excel).
      const detail = await orderQueries.orderDetailQuery(supabase, row.id).queryFn();
      if (!detail) {
        alert('Order not found.');
        return;
      }
      const isSandal = line.line_type === 'orthopedic_sandal';
      const templateKey =
        route === 'local'
          ? 'local_provider_design'
          : isSandal
            ? 'perpedes_order_sandal'
            : 'perpedes_order_insole';
      const customLine = detail.lines.find((l) => l.id === line.id) ?? detail.lines[0];
      const variables = buildDraftVariables(detail, customLine, isSandal, window.location.origin);
      const excelBytes = await getPrescriptionExcelBytes(detail);
      const result = await supplierDraftQueries.createSupplierDraft(supabase, {
        orderId: detail.id,
        orderLineId: line.id,
        templateKey,
        variables,
        excelBytes,
        excelFilename: prescriptionExcelFilename(detail),
      });
      const skipped = result.skipped_attachments ?? [];
      const skippedNote =
        skipped.length > 0
          ? `\n\nNOTE: ${skipped.length} file(s) were too large for the email and were NOT attached (attach them another way):\n` +
            skipped.map((s) => `  - ${s.filename} (${s.size_mb} MB)`).join('\n')
          : '';
      alert(
        `Draft created in Gmail (${result.account}).\n` +
          `To: ${result.to}\n` +
          `Attachments: ${result.attachment_count}${skippedNote}\n\n` +
          'Open Gmail, then Drafts, to review and send.',
      );
    } catch (e) {
      alert(`Draft creation failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setCreatingDraft(false);
    }
  };

  const handleDelete = () => {
    if (deleteDisabledByStatus) return;
    if (
      confirm(
        `Move order ${row.order_number} to the recycle bin?\n\nIt disappears from every list straight away. An INSOLECH admin can restore it for 60 days, after which it can be emptied for good. Cancelling the order is still usually safer.`,
      )
    ) {
      deleteMut.mutate();
    }
  };

  /* ------------------------------------------------------------------ */
  /* Batch 4 — mobile card variant (sm:hidden layer in the tracker).     */
  /* Same data renders as the table: resolvePatientDisplay,              */
  /* formatProductLabel, and the exact read-only StatusBadge.            */
  /* ------------------------------------------------------------------ */
  if (variant === 'card') {
    const patient = resolvePatientDisplay(row);
    // Shared inner content; phrasing-content only (spans) so it is valid
    // inside the <button> body.
    const cardContent = (
      <>
        <span className="flex items-center justify-between gap-2">
          <span className="font-medium text-text-primary truncate">
            {!isAgentView && patient.name ? patient.name : (row.order_number ?? '-')}
            {!isAgentView && patient.isLinePatient ? (
              <span className="text-xs text-text-muted font-normal"> (line patient)</span>
            ) : null}
            {!isAgentView && patient.extraCount > 0 ? (
              <span className="text-xs text-text-muted font-normal"> +{patient.extraCount} more</span>
            ) : null}
          </span>
          <StatusBadge
            status={(STATUS_TO_KEY[row.status] ?? 'draft') as StatusKey}
            size="sm"
          />
        </span>
        <span className="text-xs text-text-muted truncate">
          {!isAgentView && patient.name ? (
            <>
              <span data-testid="order-card-number">{row.order_number ?? '-'}</span>
              {' · '}
            </>
          ) : null}
          {formatProductLabel(row.primary_line)}
        </span>
      </>
    );
    const bodyClass =
      'flex-1 min-w-0 text-start p-3 flex flex-col gap-1.5 rounded-s-lg';
    return (
      <div
        className={`rounded-lg border border-border-subtle flex items-stretch ${row.is_urgent ? 'bg-warning-50' : 'bg-surface-elevated'}`}
        data-testid="order-card"
      >
        {/* Tap target: the whole card body opens the order detail. Agents
            get a non-interactive body (onRowClick is a deliberate no-op in
            the agent scope, mirroring the table's non-clickable cells). */}
        {isAgentView ? (
          <div className={bodyClass} data-testid="order-card-body">
            {cardContent}
          </div>
        ) : (
          <button
            type="button"
            onClick={onView}
            className={`${bodyClass} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500`}
            data-testid="order-card-body"
          >
            {cardContent}
          </button>
        )}
        {/* Actions menu: mirrors the table's icon cluster exactly (same
            handlers, same eligibility, same confirms). Hidden for agents,
            like the table's Actions column.
            modal={false}: Radix-modal menus set pointer-events:none on the
            body while open; a blocking confirm() in onSelect + the row
            unmounting on mutation success can orphan that lock and freeze
            the page (same bug ProfileMenu patched). Non-modal menus never
            touch body pointer-events. The menu is CONTROLLED so async
            actions can hold it open and show their busy label, then close
            themselves - otherwise the disabled/busy states would be
            invisible the moment the menu auto-closed. */}
        {!isAgentView ? (
          <DropdownMenu modal={false} open={cardMenuOpen} onOpenChange={setCardMenuOpen}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Order actions"
                data-testid="order-card-menu"
                className="w-11 shrink-0 flex items-center justify-center rounded-e-lg text-text-muted hover:text-text-primary hover:bg-sand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 border-s border-border-subtle"
              >
                <MoreVertical className="h-5 w-5" aria-hidden="true" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={4} className="w-56">
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  setCardMenuOpen(false);
                  onView();
                }}
              >
                <Eye className="h-4 w-4" aria-hidden="true" /> View order
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={urgentMut.isPending}
                onSelect={(e) => {
                  e.preventDefault();
                  setCardMenuOpen(false);
                  urgentMut.mutate(!row.is_urgent);
                }}
              >
                <Flag className="h-4 w-4" aria-hidden="true" />{' '}
                {row.is_urgent ? 'Clear urgent flag' : 'Mark as urgent'}
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={duplicateMut.isPending}
                onSelect={(e) => {
                  e.preventDefault();
                  setCardMenuOpen(false);
                  handleDuplicate();
                }}
              >
                <Copy className="h-4 w-4" aria-hidden="true" /> Duplicate order
              </DropdownMenuItem>
              {isAdmin ? (
                <DropdownMenuItem
                  disabled={exportingExcel}
                  onSelect={(e) => {
                    e.preventDefault();
                    void (async () => {
                      try {
                        await handleExportExcel();
                      } finally {
                        setCardMenuOpen(false);
                      }
                    })();
                  }}
                >
                  <FileSpreadsheet className="h-4 w-4" aria-hidden="true" />
                  {exportingExcel ? 'Exporting Excel...' : 'Export prescription to Excel'}
                </DropdownMenuItem>
              ) : null}
              {canMailDraft ? (
                <DropdownMenuItem
                  disabled={creatingDraft}
                  onSelect={(e) => {
                    e.preventDefault();
                    void (async () => {
                      try {
                        await handleCreateSupplierDraft();
                      } finally {
                        setCardMenuOpen(false);
                      }
                    })();
                  }}
                >
                  <Mail className="h-4 w-4" aria-hidden="true" />
                  {creatingDraft
                    ? 'Creating draft...'
                    : row.primary_line?.production_path === 'eu'
                      ? 'Draft email to supplier'
                      : 'Draft email to local designer'}
                </DropdownMenuItem>
              ) : null}
              {canWorkshopPdf ? (
                <DropdownMenuItem
                  disabled={downloadingPdf}
                  onSelect={(e) => {
                    e.preventDefault();
                    void (async () => {
                      setDownloadingPdf(true);
                      try {
                        await downloadWorkshopPdf(row.id, row.primary_line?.id);
                      } finally {
                        setDownloadingPdf(false);
                        setCardMenuOpen(false);
                      }
                    })();
                  }}
                >
                  <Download className="h-4 w-4" aria-hidden="true" />
                  {downloadingPdf ? 'Preparing PDF...' : 'Workshop PDF'}
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem
                variant="destructive"
                disabled={deleteMut.isPending || deleteDisabledByStatus}
                onSelect={(e) => {
                  e.preventDefault();
                  setCardMenuOpen(false);
                  handleDelete();
                }}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                {deleteDisabledByStatus ? 'Delete (draft/submitted only)' : 'Delete order'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
    );
  }

  return (
    <TableRow
      className={row.is_urgent ? 'cursor-default bg-warning-50/60' : 'cursor-default'}
      data-urgent={row.is_urgent ? 'true' : undefined}
    >
      {/* Sticky-left Patient column. Background must be opaque so it
          masks the cells underneath when scrolled horizontally.
          Hidden in agent view; Order ID takes the sticky slot instead.
          Task B5: marketplace orders (FITO / ready-made / orthopaedic)
          have no header patient; fall back to the first line.patient_name
          when present, with a small disambiguator. */}
      {!isAgentView ? (() => {
        const patient = resolvePatientDisplay(row);
        return (
          <TableCell
            // max-sm: tighter clamp - at 200px the sticky patient cell ate
            // over half the 390px scroll window.
            className={`py-2 font-medium text-text-primary whitespace-nowrap truncate max-w-[200px] max-sm:max-w-[124px] sticky start-0 z-10 cursor-pointer ${row.is_urgent ? 'bg-warning-50' : 'bg-surface-elevated'}`}
            title={patient.name ?? undefined}
            onClick={onView}
          >
            {patient.name ? (
              <span className="inline-flex items-center gap-1.5">
                <span className="truncate">{patient.name}</span>
                {patient.isLinePatient ? (
                  <span className="text-xs text-text-muted font-normal whitespace-nowrap">
                    (line patient)
                  </span>
                ) : null}
                {patient.extraCount > 0 ? (
                  <span className="text-xs text-text-muted font-normal whitespace-nowrap">
                    +{patient.extraCount} more
                  </span>
                ) : null}
              </span>
            ) : (
              <span className="text-text-muted">-</span>
            )}
          </TableCell>
        );
      })() : null}
      {/* W3: agent view collapses the Order ID cell to a non-clickable
          label (no cursor-pointer, no hover:underline, no onClick). The
          row click handler (onRowClick) also early-returns for agents,
          so there is no fallback navigation path. */}
      <TableCell
        className={[
          'py-2 font-mono text-xs text-text-primary whitespace-nowrap',
          isAgentView
            ? `sticky start-0 z-10 ${row.is_urgent ? 'bg-warning-50' : 'bg-surface-elevated'}`
            : 'cursor-pointer hover:underline',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={isAgentView ? undefined : onView}
      >
        {row.order_number ?? '-'}
      </TableCell>
      {/* W4: Patient ID cell removed (short_code column dropped). */}
      {/* Task B1: Product slot lives between Order ID and Status.
          Renders the per-line product label resolved from
          configuration.material / display_label / product_name. */}
      <TableCell className="py-2 whitespace-nowrap truncate max-w-[200px]">
        <span className="inline-flex items-center gap-1.5">
          {formatProductLabel(row.primary_line)}
          {row.lines.some((l) => l.has_active_token) ? (
            <span title="Has active QR / NFC tag" aria-label="Has active QR / NFC tag">
              <QrCode className="h-3.5 w-3.5 shrink-0 text-text-muted" aria-hidden="true" />
            </span>
          ) : null}
        </span>
      </TableCell>
      {/* Qty: total units/pairs across ALL lines (see lineQtyTotal), so
          multi-line marketplace orders are not understated by a
          primary-line-only read. Muted for the common single-unit case;
          >1 is the exception the lab must catch, so it gets weight. */}
      <TableCell className="py-2 tabular whitespace-nowrap text-center">
        {(() => {
          const total = lineQtyTotal(row);
          if (total == null) return <span className="text-text-muted">-</span>;
          return total > 1 ? (
            <span className="font-semibold text-text-primary">{total}</span>
          ) : (
            <span className="text-text-muted">{total}</span>
          );
        })()}
      </TableCell>
      <TableCell className="py-2 whitespace-nowrap">
        {/* Task B6: agents see a simplified Confirmed / Not confirmed
            pill instead of the raw enum; admin + podiatrist views keep
            the inline-edit / raw status badge. The agent pill is plain
            static text (no chevron, no click handler), and the admin
            inline-edit dropdown is gated behind canEditCells so the
            agent path can never fall through to an editable control. */}
        {isAgentView ? (() => {
          const d = agentStatusDisplay(row.status);
          return (
            <span
              className={[
                'inline-flex items-center text-xs font-medium rounded-full px-2.5 py-1 border',
                d.className,
              ].join(' ')}
            >
              {d.label}
            </span>
          );
        })() : (() => {
          // Task C: podiatrists can flip their own pending orders between
          // 'draft' and 'submitted' inline. Admin keeps the full enum.
          // For podiatrists the write goes through the SECURITY DEFINER
          // RPC set_podiatrist_order_status (migration 20261115000000) so
          // it works even when the podiatrist owns a direct-billed order
          // placed against a clinic she is not a member of, and so the
          // cascade-derived orders.status is durable across later line
          // edits. See statusMut above for the full rationale.
          const isOwnPodiatristPending =
            !isAdmin
            && currentUserId !== null
            && row.podiatrist_id === currentUserId
            && (row.status === 'draft' || row.status === 'submitted');
          const canEditStatus = canEditCells && (isAdmin || isOwnPodiatristPending);
          // Item 7: mark a draft the wizard flagged INCOMPLETE. It can't be
          // flipped to submitted from here (server-gated) - the operator must
          // reopen it in the wizard (Continue) to finish + submit.
          const incompleteDraft = row.status === 'draft' && row.draft_complete === false;
          const incompletePill = incompleteDraft ? (
            <span
              className="inline-flex items-center rounded-full bg-warning-100 text-warning-800 px-1.5 py-0.5 text-[10px] font-medium"
              title="Some required prescription fields are missing. Reopen it in the wizard (Continue) to complete and submit."
            >
              Incomplete
            </span>
          ) : null;
          if (!canEditStatus) {
            return (
              <div className="flex items-center gap-1.5">
                <StatusBadge
                  status={(STATUS_TO_KEY[row.status] ?? 'draft') as StatusKey}
                  size="sm"
                />
                {incompletePill}
              </div>
            );
          }
          return (
            <div className="flex items-center gap-1.5">
              <InlineStatusSelect
                value={row.status}
                disabled={statusMut.isPending}
                options={isAdmin ? STATUS_OPTIONS : PODIATRIST_STATUS_OPTIONS}
                onChange={(next) => statusMut.mutate(next)}
              />
              {incompletePill}
            </div>
          );
        })()}
      </TableCell>
      {/* Order date - admin editable (date-only; preserves the time
          component by re-writing midnight UTC if changed). Gated by
          canEditCells so the agent view stays read-only even on the
          off-chance the viewer also has admin rights. */}
      <TableCell className="py-2 text-text-secondary whitespace-nowrap max-md:hidden">
        {isAdmin && canEditCells ? (
          <InlineDateInput
            value={row.created_at ? row.created_at.slice(0, 10) : null}
            disabled={orderDateMut.isPending}
            onChange={(d) => {
              if (d) orderDateMut.mutate(`${d}T00:00:00.000Z`);
            }}
          />
        ) : (
          formatDate(row.created_at)
        )}
      </TableCell>
      {/* Expected delivery hidden in agent view. Inline editor gated
          on canEditCells in case the column is later un-hidden for
          agents, the agent path must never get an editable picker. */}
      {!isAgentView ? (
        <TableCell className="py-2 tabular text-text-secondary whitespace-nowrap max-md:hidden">
          {isAdmin && canEditCells ? (
            <>
              <InlineDateInput
                value={row.expected_delivery_date}
                disabled={dateMut.isPending || announcedGuard.checking}
                onChange={(d) =>
                  announcedGuard.guard({
                    orderId: row.id,
                    nextDate: d,
                    // mutateAsync: the guard must not claim success until the
                    // write has actually landed.
                    apply: () => dateMut.mutateAsync(d),
                  })
                }
              />
              {/* Portalled modal + toast: renders nothing into this cell. */}
              {announcedGuard.element}
            </>
          ) : row.expected_delivery_date ? (
            formatDate(row.expected_delivery_date)
          ) : (
            '-'
          )}
        </TableCell>
      ) : null}
      {/* Podiatrist column stays on wideView (admin + agent only). For a
          multi-clinic podiatrist the podiatrist is always themselves, so
          surfacing it would only add noise. */}
      {wideView ? (
        <TableCell className="py-2 whitespace-nowrap truncate max-w-[180px]">
          {isAdmin && canEditCells ? (
            <InlinePodiatristSelect
              clinicId={row.clinic_id}
              value={row.podiatrist?.id ?? null}
              currentName={row.podiatrist?.full_name ?? null}
              disabled={podiatristMut.isPending}
              error={podiatristError}
              onChange={(id) => podiatristMut.mutate(id)}
            />
          ) : (
            <span className="text-sm text-text-secondary">
              {row.podiatrist?.full_name ?? '-'}
            </span>
          )}
        </TableCell>
      ) : null}
      {/* R35: inline clinic re-assign. Admin gets the full clinic
          list via a lazy RPC. Non-admin members get to pick among
          the clinics they belong to (so a podiatrist working at
          clinic A + B can re-link an order without admin help).
          Agent view collapses to a plain text label of the current
          clinic; agents cannot move an order between clinics.
          Visibility now mirrors showClinicColumn so multi-clinic
          podiatrists also see the column. */}
      {showClinicColumn ? (
        <TableCell className="py-2 whitespace-nowrap truncate max-w-[200px]">
          {isAdmin ? (
            // Admin: unified billing reassignment. The dropdown offers
            // "Direct Billing" + every clinic; crossing the billing-mode
            // boundary routes through reassign_order_billing (with a typed
            // confirmation when the patient already paid), while a plain
            // clinic-to-clinic pick just re-points clinic_id.
            <AdminClinicReassign row={row} onDone={invalidate} />
          ) : row.is_direct_billed ? (
            // Non-admin: direct-billed orders are not tied to a clinic invoice,
            // so the re-assign control would be meaningless - render a badge.
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-clay-50 text-clay-700 border border-clay-200"
              title="Direct billing (no clinic invoice)"
            >
              Direct Billing
            </span>
          ) : canEditCells ? (
            <InlineClinicSelect
              value={row.clinic?.id ?? null}
              currentName={row.clinic?.name ?? null}
              userClinics={userClinics}
              showAllClinics={isAdmin}
              disabled={clinicMut.isPending}
              onChange={(id) => clinicMut.mutate(id)}
            />
          ) : (
            <span className="text-sm text-text-secondary truncate inline-block max-w-[200px]">
              {row.clinic?.name ?? '-'}
            </span>
          )}
        </TableCell>
      ) : null}
      {/* Task B1: Product cell relocated above (between Order ID and
          Status). Previously rendered here after Clinic. */}
      {/* Route column hidden in agent view. */}
      {wideView && !isAgentView ? (
        <TableCell className="py-2 whitespace-nowrap">
          <RouteCell
            row={row}
            isAdmin={isAdmin}
            onOpen={() => setRouteModalOpen(true)}
          />
          {isAdmin && row.primary_line?.id ? (
            <RouteEditModal
              open={routeModalOpen}
              onOpenChange={setRouteModalOpen}
              orderNumber={row.order_number}
              lineId={row.primary_line.id}
              initialPath={(row.primary_line.production_path ?? 'eu') as 'eu' | 'local'}
              initialEuProviderId={row.primary_line.assigned_provider?.id ?? null}
              onSaved={invalidate}
            />
          ) : null}
        </TableCell>
      ) : null}
      {/* Actions cluster hidden in agent view. */}
      {!isAgentView ? (
        <TableCell className="py-2 whitespace-nowrap text-end">
        <div className="inline-flex items-center gap-1">
          <button
            type="button"
            title={row.is_urgent ? 'Clear urgent flag' : 'Mark as urgent'}
            aria-pressed={row.is_urgent}
            onClick={() => urgentMut.mutate(!row.is_urgent)}
            disabled={urgentMut.isPending}
            className={`inline-flex items-center justify-center h-7 w-7 max-sm:h-10 max-sm:w-10 rounded-md ${
              row.is_urgent
                ? 'text-warning-700 bg-warning-50'
                : 'text-text-muted hover:text-warning-700 hover:bg-warning-50'
            }`}
            aria-label={row.is_urgent ? 'Clear urgent flag' : 'Mark as urgent'}
            data-testid={`urgent-toggle-${row.order_number}`}
          >
            <Flag className="h-4 w-4" fill={row.is_urgent ? 'currentColor' : 'none'} />
          </button>
          <button
            type="button"
            title="View order"
            onClick={onView}
            className="inline-flex items-center justify-center h-7 w-7 max-sm:h-10 max-sm:w-10 rounded-md text-text-muted hover:text-brand-700 hover:bg-brand-50"
            aria-label="View order"
          >
            <Eye className="h-4 w-4" />
          </button>
          <button
            type="button"
            title="Duplicate order"
            disabled={duplicateMut.isPending}
            onClick={handleDuplicate}
            className="inline-flex items-center justify-center h-7 w-7 max-sm:h-10 max-sm:w-10 rounded-md text-text-muted hover:text-brand-700 hover:bg-brand-50 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:text-text-muted disabled:hover:bg-transparent"
            aria-label="Duplicate order"
          >
            <Copy className="h-4 w-4" />
          </button>
          {isAdmin ? (
            <button
              type="button"
              title="Export prescription to Excel"
              disabled={exportingExcel}
              onClick={() => void handleExportExcel()}
              className="inline-flex items-center justify-center h-7 w-7 max-sm:h-10 max-sm:w-10 rounded-md text-text-muted hover:text-brand-700 hover:bg-brand-50 disabled:opacity-30"
              aria-label="Export prescription to Excel"
            >
              <FileSpreadsheet className="h-4 w-4" />
            </button>
          ) : null}
          {/* Generate supplier draft email — admin-only, custom orders that
              have a route + provider (EU needs an assigned provider; local
              needs a route). premium_3d / refurbishment / ready-made / OTS
              have no production_path, so the button is hidden for them. */}
          {canMailDraft ? (
            <button
              type="button"
              title={
                row.primary_line?.production_path === 'eu'
                  ? `Generate draft email to ${row.primary_line?.assigned_provider?.name ?? 'the supplier'}`
                  : 'Generate draft email to the local designer'
              }
              disabled={creatingDraft}
              onClick={() => void handleCreateSupplierDraft()}
              className="inline-flex items-center justify-center h-7 w-7 max-sm:h-10 max-sm:w-10 rounded-md text-text-muted hover:text-brand-700 hover:bg-brand-50 disabled:opacity-30"
              aria-label="Generate supplier draft email"
            >
              <Mail className="h-4 w-4" />
            </button>
          ) : null}
          {/* Workshop PDF, admin-only, custom_insole / premium_3d_insole
              only. Anything else (sandals, FITO, OTS) doesn't have a deep
              prescription state worth printing for the lab. */}
          {canWorkshopPdf ? (
            <WorkshopPdfButton
              orderId={row.id}
              primaryLineId={row.primary_line?.id}
            />
          ) : null}
          {/* W6: Delete button now surfaces for podiatrists too. It is
              enabled only while the order is still pending (draft /
              submitted); once production has started the button is
              disabled with a tooltip explaining why. Admin can always
              delete (admin behaviour unchanged). The mutation is the
              existing deleteMut; RLS gates whether the delete actually
              affects rows (a matching podiatrist-delete-own-pending
              policy must exist server-side, otherwise the click
              silently affects zero rows and the existing onError
              alert surfaces the empty result). The button is rendered
              inside the Actions cluster, which is already hidden for
              the agent view. */}
          <button
            type="button"
            title={
              deleteDisabledByStatus
                ? 'You can only delete draft or submitted orders. Cancel the order instead.'
                : 'Delete order'
            }
            disabled={deleteMut.isPending || deleteDisabledByStatus}
            onClick={handleDelete}
            className="inline-flex items-center justify-center h-7 w-7 max-sm:h-10 max-sm:w-10 rounded-md text-text-muted hover:text-error-700 hover:bg-error-50 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:text-text-muted disabled:hover:bg-transparent"
            aria-label="Delete order"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </TableCell>
      ) : null}
    </TableRow>
  );
}

// R28 - selectable status set simplified. in_review / on_hold / cancelled /
// returned were removed from this list so the inline-edit dropdown + the
// column-filter popover only surface the five day-to-day statuses. Legacy
// rows still render correctly because STATUS_LABEL/STATUS_PILL below keep
// entries for the removed values.
const STATUS_OPTIONS = [
  'draft',
  'submitted',
  'in_production',
  'in_delivery',
  'delivered',
] as const;

// Task C: the inline dropdown a non-admin podiatrist sees for their own
// pending orders. Limited to the two values they can legitimately toggle
// between before production starts. Anything beyond 'submitted' is admin-
// only territory and is still gated server-side by the existing RLS
// (orders_clinic_or_admin_update) + state-machine triggers.
const PODIATRIST_STATUS_OPTIONS = ['draft', 'submitted'] as const;

const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  in_review: 'In review',
  in_production: 'In production',
  in_delivery: 'In delivery',
  delivered: 'Delivered',
  on_hold: 'On hold',
  cancelled: 'Cancelled',
  returned: 'Returned',
};

const STATUS_PILL: Record<string, string> = {
  draft: 'bg-sand-100 text-text-secondary border-border-subtle',
  submitted: 'bg-info-50 text-info-700 border-info-200',
  in_review: 'bg-clay-50 text-clay-700 border-clay-200',
  in_production: 'bg-clay-50 text-clay-700 border-clay-200',
  in_delivery: 'bg-warning-50 text-warning-800 border-warning-200',
  delivered: 'bg-success-50 text-success-700 border-success-200',
  on_hold: 'bg-warning-50 text-warning-800 border-warning-200',
  cancelled: 'bg-error-50 text-error-700 border-error-200',
  returned: 'bg-error-50 text-error-700 border-error-200',
};

function InlineStatusSelect({
  value,
  disabled,
  onChange,
  options = STATUS_OPTIONS,
}: {
  value: string;
  disabled: boolean;
  onChange: (v: string) => void;
  /** Selectable status values. Defaults to the admin five-status set;
   *  podiatrists get a narrower {draft, submitted} list (Task C). The
   *  current value is always rendered even if it falls outside `options`
   *  so the dropdown can still show the row's true status. */
  options?: ReadonlyArray<string>;
}) {
  // Preserve the current value as an extra option when it sits outside
  // the supplied set (e.g. an admin previously pushed a podiatrist's order
  // into 'in_production' and the row is still rendered with the narrower
  // podiatrist option list); without this, the <select> would silently
  // collapse the value to the first option on first render.
  const renderedOptions = options.includes(value) ? options : [value, ...options];
  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => {
        if (e.target.value !== value) onChange(e.target.value);
      }}
      className={[
        'inline-flex items-center text-xs font-medium rounded-full px-2.5 py-1 border cursor-pointer',
        'focus:outline-none focus:ring-2 focus:ring-brand-500',
        STATUS_PILL[value] ?? STATUS_PILL.draft,
      ].join(' ')}
    >
      {renderedOptions.map((s) => (
        <option key={s} value={s} className="bg-white text-text-primary">
          {STATUS_LABEL[s] ?? s}
        </option>
      ))}
    </select>
  );
}

function InlineDateInput({
  value,
  disabled,
  onChange,
}: {
  value: string | null;
  disabled: boolean;
  onChange: (v: string | null) => void;
}) {
  return (
    <input
      type="date"
      value={value ?? ''}
      disabled={disabled}
      onChange={(e) => {
        const next = e.target.value || null;
        if (next !== value) onChange(next);
      }}
      className="text-sm bg-transparent border border-transparent hover:border-border-subtle hover:bg-sand-50 focus:border-brand-500 focus:bg-white focus:outline-none rounded px-1.5 py-0.5 cursor-pointer text-text-secondary"
    />
  );
}

/**
 * InlineClinicSelect — R35. Admin gets the full clinic list (lazy fetched
 * via adminClinicListQuery). Non-admin members get the clinics they belong
 * to via the auth context. The select is disabled when the resulting list
 * is empty (e.g. user has only one clinic AND is not admin — in that case
 * the row text just shows the current clinic name).
 */
function InlineClinicSelect({
  value,
  currentName,
  userClinics,
  showAllClinics,
  disabled,
  onChange,
}: {
  value: string | null;
  currentName: string | null;
  userClinics: ReadonlyArray<{ id: string; name: string }>;
  showAllClinics: boolean;
  disabled: boolean;
  onChange: (id: string) => void;
}) {
  // Admin: pull the full clinic catalog. Throttle visibility via `enabled`
  // so we don't hit the RPC for podiatrists.
  const allClinicsQuery = useQuery({
    ...adminQueries.adminClinicListQuery(supabase, { page: 0, pageSize: 500 }),
    enabled: showAllClinics,
  });

  const options = React.useMemo(() => {
    if (showAllClinics) {
      const rows = allClinicsQuery.data?.rows ?? [];
      return rows.map((r) => ({ id: r.id, name: r.name }));
    }
    return [...userClinics];
  }, [showAllClinics, allClinicsQuery.data, userClinics]);

  // If the user only has one clinic and isn't admin, the select is a noop.
  // Render plain text so we don't add visual noise to the row.
  if (!showAllClinics && options.length <= 1) {
    return (
      <span className="text-sm text-text-secondary truncate inline-block max-w-[180px]">
        {currentName ?? '-'}
      </span>
    );
  }

  // Defensively show the current clinic option even if it's not in the
  // loaded list (e.g. admin querying a clinic outside the first 500 rows).
  const hasCurrent = options.some((o) => o.id === value);

  return (
    <select
      value={value ?? ''}
      disabled={disabled || (showAllClinics && allClinicsQuery.isLoading)}
      onChange={(e) => {
        const next = e.target.value;
        if (next && next !== value) onChange(next);
      }}
      className="text-sm bg-transparent border border-transparent hover:border-border-subtle hover:bg-sand-50 focus:border-brand-500 focus:bg-white focus:outline-none rounded px-1.5 py-0.5 cursor-pointer text-text-secondary max-w-[200px]"
    >
      {value && !hasCurrent ? (
        <option value={value}>{currentName ?? '(current)'}</option>
      ) : null}
      {options.map((c) => (
        <option key={c.id} value={c.id}>
          {c.name}
        </option>
      ))}
    </select>
  );
}

/**
 * AdminClinicReassign — admin-only billing reassignment control in the tracker's
 * Clinic column. The dropdown lists "Direct Billing" + every clinic. Selecting:
 *   - a clinic while direct-billed  -> convert to a clinic order
 *   - "Direct Billing" while a clinic order -> convert to direct billing
 *   - a different clinic while a clinic order -> plain clinic move
 * All routes call reassign_order_billing (server-enforced rules). A direct-billed
 * order the PATIENT already paid returns needs_confirmation; we then require the
 * admin to TYPE the order number, and on confirm the order is reassigned with a
 * persistent zoho_invoice_blocked flag (no double billing). Reorders are rejected
 * server-side; the error surfaces inline.
 */
function AdminClinicReassign({
  row,
  onDone,
}: {
  row: {
    id: string;
    order_number: string;
    is_direct_billed: boolean;
    clinic?: { id: string; name: string | null } | null;
  };
  onDone: () => void;
}) {
  const allClinicsQuery = useQuery({
    ...adminQueries.adminClinicListQuery(supabase, { page: 0, pageSize: 500 }),
  });
  const clinics = React.useMemo(
    () => (allClinicsQuery.data?.rows ?? []).map((r) => ({ id: r.id, name: r.name })),
    [allClinicsQuery.data],
  );

  const [pending, setPending] = React.useState<
    { targetClinicId: string; toDirectBilled: boolean; label: string } | null
  >(null);
  const [confirmText, setConfirmText] = React.useState('');
  const [err, setErr] = React.useState<string | null>(null);

  const errMsg = (e: unknown): string =>
    e instanceof Error
      ? e.message
      : e && typeof e === 'object' && 'message' in e
        ? String((e as Record<string, unknown>).message)
        : String(e);

  const mut = useMutation({
    mutationFn: (v: {
      targetClinicId: string;
      toDirectBilled: boolean;
      confirm: boolean;
      label: string;
    }) =>
      orderMutations.reassignOrderBilling(supabase, {
        orderId: row.id,
        targetClinicId: v.targetClinicId,
        toDirectBilled: v.toDirectBilled,
        confirm: v.confirm,
      }),
    onSuccess: (res, v) => {
      if (res.status === 'needs_confirmation') {
        setPending({ targetClinicId: v.targetClinicId, toDirectBilled: v.toDirectBilled, label: v.label });
        return;
      }
      setPending(null);
      setConfirmText('');
      onDone();
    },
    onError: (e) => setErr(errMsg(e)),
  });

  const currentValue = row.is_direct_billed ? 'DIRECT' : row.clinic?.id ?? '';
  const hasCurrentClinic = !!row.clinic?.id && clinics.some((c) => c.id === row.clinic?.id);

  const onSelect = (next: string) => {
    setErr(null);
    if (next === currentValue) return;
    if (next === 'DIRECT') {
      // clinic -> direct billing; keep the current clinic as the target.
      if (row.is_direct_billed || !row.clinic?.id) return;
      mut.mutate({ targetClinicId: row.clinic.id, toDirectBilled: true, confirm: false, label: 'Direct Billing' });
    } else {
      const label = clinics.find((c) => c.id === next)?.name ?? 'the selected clinic';
      // direct -> clinic, OR plain clinic move (RPC picks the branch).
      mut.mutate({ targetClinicId: next, toDirectBilled: false, confirm: false, label });
    }
  };

  const confirmDisabled = confirmText.trim() !== row.order_number || mut.isPending;

  return (
    <>
      <select
        value={currentValue}
        disabled={mut.isPending || allClinicsQuery.isLoading}
        onChange={(e) => onSelect(e.target.value)}
        title="Reassign billing (admin)"
        className="text-sm bg-transparent border border-transparent hover:border-border-subtle hover:bg-sand-50 focus:border-brand-500 focus:bg-white focus:outline-none rounded px-1.5 py-0.5 cursor-pointer text-text-secondary max-w-[200px]"
      >
        <option value="DIRECT">Direct Billing</option>
        {row.clinic?.id && !hasCurrentClinic ? (
          <option value={row.clinic.id}>{row.clinic.name ?? '(current clinic)'}</option>
        ) : null}
        {clinics.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
      {err ? <span className="ms-1 text-xs text-error-600">{err}</span> : null}

      <Modal
        open={pending != null}
        onOpenChange={(o) => {
          if (!o) {
            setPending(null);
            setConfirmText('');
            setErr(null);
          }
        }}
      >
        <ModalContent size="md">
          <ModalHeader>
            <ModalTitle>Reassign a paid order?</ModalTitle>
            <ModalDescription>
              Order {row.order_number} was already paid by the patient.
            </ModalDescription>
          </ModalHeader>
          <ModalBody className="space-y-3">
            <Alert tone="warning" title="This order was already paid by the patient">
              Reassigning it to <strong>{pending?.label}</strong> makes it a clinic order. To prevent
              double billing, clinic invoicing will be BLOCKED for it. You can clear that block later
              from the order detail page.
            </Alert>
            <p className="text-sm text-text-secondary">
              Type the order number <strong>{row.order_number}</strong> to confirm.
            </p>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={row.order_number}
              aria-label="Type the order number to confirm"
            />
            {err ? <p className="text-xs text-error-600">{err}</p> : null}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setPending(null);
                setConfirmText('');
                setErr(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={confirmDisabled}
              onClick={() => {
                if (!pending) return;
                mut.mutate({ ...pending, confirm: true });
              }}
            >
              Reassign and block invoicing
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

/**
 * Copy for a failed podiatrist reassignment. The RPC's 23514 means the
 * chosen account is not an active podiatrist member of the ORDER's clinic;
 * the dropdown is clinic-scoped, so this only happens when the membership
 * changed under the admin's feet. Everything else (42501, P0002, transport)
 * surfaces its own message. Exported additively for the select's test.
 */
export function podiatristReassignMessage(e: unknown): string {
  const code = (e as { code?: unknown } | null)?.code;
  if (code === '23514') {
    return "That account is not an active podiatrist of this order's clinic";
  }
  return e instanceof Error ? e.message : String(e);
}

/**
 * InlinePodiatristSelect - admin-only inline re-assign for the order's
 * podiatrist. Lists the ACTIVE podiatrist members of the ORDER's clinic
 * (clinicPodiatristsQuery), never the platform-wide user roster, so the
 * dropdown only offers accounts reassign_order_podiatrist will accept.
 * Always renders the current holder as a defensive option even when they
 * are not in that list (an admin who placed the order, or a member since
 * deactivated).
 *
 * The clinic roster is loaded LAZILY: an admin tracker page can span many
 * clinics, and one query per clinic on mount (three requests each) is far
 * more than the single shared roster call this replaced. Nothing is fetched
 * until the admin first touches THIS row's select (focus, mouse or pointer
 * press); until then it lists only the current holder and stays enabled so
 * it can receive that touch, then it is disabled while the list is in
 * flight. Query keys dedupe per clinic, so later rows of the same clinic
 * reuse the cached list. Exported additively for its test.
 */
export function InlinePodiatristSelect({
  clinicId,
  value,
  currentName,
  disabled,
  error,
  onChange,
}: {
  /** The ORDER's clinic: the only membership the RPC accepts. */
  clinicId: string;
  value: string | null;
  currentName: string | null;
  disabled: boolean;
  /** Inline failure copy from the last reassignment attempt, if any. */
  error?: string | null;
  onChange: (id: string) => void;
}) {
  // Armed by the first interaction with this row's select; idempotent.
  const [armed, setArmed] = React.useState(false);
  const arm = () => setArmed(true);
  const podiatristsQuery = useQuery({
    ...adminQueries.clinicPodiatristsQuery(supabase, clinicId),
    enabled: armed,
  });

  const podiatrists = React.useMemo(
    () => (podiatristsQuery.data ?? []).map((p) => ({ id: p.user_id, name: p.full_name })),
    [podiatristsQuery.data],
  );

  const hasCurrent = value ? podiatrists.some((p) => p.id === value) : true;

  return (
    <>
      <select
        value={value ?? ''}
        // isLoading is pending AND fetching: false while un-armed (no
        // request), true only once the armed fetch is in flight.
        disabled={disabled || podiatristsQuery.isLoading}
        onFocus={arm}
        onMouseDown={arm}
        onPointerDown={arm}
        onChange={(e) => {
          const next = e.target.value;
          if (next && next !== value) onChange(next);
        }}
        className="text-sm bg-transparent border border-transparent hover:border-border-subtle hover:bg-sand-50 focus:border-brand-500 focus:bg-white focus:outline-none rounded px-1.5 py-0.5 cursor-pointer text-text-secondary max-w-[180px]"
      >
        {value && !hasCurrent ? (
          <option value={value}>{currentName ?? '(current)'}</option>
        ) : null}
        {podiatrists.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
      {error ? <span className="ms-1 text-xs text-error-600">{error}</span> : null}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*  Route cell + modal                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Map a production_path value to its display label. Returns null for null
 * input so the column-filter popover can skip rows without a route (e.g.
 * OTS orders that have no production path).
 */
function routeLabel(path: 'eu' | 'local' | null | undefined): string | null {
  if (path === 'eu') return 'EU end-to-end';
  if (path === 'local') return 'Local chain';
  return null;
}

/**
 * The Provider column cell. Shows the current Route as a button (admin) or
 * static text (everyone else). Clicking opens the RouteEditModal which
 * mirrors the Production box on the order detail page.
 */
function RouteCell({
  row,
  isAdmin,
  onOpen,
}: {
  row: OrderListRow;
  isAdmin: boolean;
  onOpen: () => void;
}) {
  const path = row.primary_line?.production_path ?? null;
  const label = routeLabel(path);

  // No route at all (e.g. OTS-only orders) → render an em-dash placeholder.
  if (!label) {
    return <span className="text-sm text-text-muted">—</span>;
  }

  if (!isAdmin) {
    return <span className="text-sm text-text-secondary">{label}</span>;
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      className={[
        'inline-flex items-center text-xs font-medium rounded-full px-2.5 py-1 border cursor-pointer',
        'focus:outline-none focus:ring-2 focus:ring-brand-500',
        path === 'eu'
          ? 'bg-brand-50 text-brand-800 border-brand-200 hover:bg-brand-100'
          : 'bg-clay-50 text-clay-800 border-clay-200 hover:bg-clay-100',
      ].join(' ')}
      aria-label={`Change route (currently ${label})`}
    >
      {label}
    </button>
  );
}

/**
 * RouteEditModal — admin-only modal that mirrors the OrderDetail Production
 * card. Lets the admin toggle the production path (eu / local) and assign
 * providers per-route (EU single provider, or three local-chain stages).
 *
 * All state is local until Save. On Save we:
 *   1. Write the new production_path to order_lines.production_path
 *   2. If EU: write assigned_provider_id to the line; clear stage rows
 *   3. If Local: clear assigned_provider_id; upsert stage rows for each
 *      designer/printer/workshop assignment via setLineStageProvider
 *
 * Mirrors the cascade logic from OrderProductionCard.tsx so the two surfaces
 * stay in sync.
 */
function RouteEditModal({
  open,
  onOpenChange,
  orderNumber,
  lineId,
  initialPath,
  initialEuProviderId,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  orderNumber: string;
  lineId: string;
  initialPath: 'eu' | 'local';
  initialEuProviderId: string | null;
  onSaved: () => void;
}) {
  const { user } = useAuth();

  // Local form state — only sent to the server on Save. Reset to the row's
  // current values whenever the modal re-opens so re-opening doesn't show
  // stale picks from a previous session.
  const [path, setPath] = React.useState<'eu' | 'local'>(initialPath);
  const [euProviderId, setEuProviderId] = React.useState<string | null>(initialEuProviderId);
  const [stageAssignments, setStageAssignments] = React.useState<
    Record<LocalChainStage, string | null>
  >({ designer: null, printer: null, workshop: null });

  const providersQuery = useQuery({
    ...adminQueries.adminProviderListQuery(supabase, { activeFilter: 'active' }),
    enabled: open,
  });
  const assignmentsQuery = useQuery({
    ...lineProviderAssignmentQueries.lineProviderAssignmentsQuery(supabase, lineId),
    enabled: open && lineId !== '',
  });

  // When the modal opens (or the row's initial values change), sync local
  // state to the source of truth so the form reflects the current DB state.
  React.useEffect(() => {
    if (!open) return;
    setPath(initialPath);
    setEuProviderId(initialEuProviderId);
  }, [open, initialPath, initialEuProviderId]);

  React.useEffect(() => {
    if (!open) return;
    const rows = assignmentsQuery.data ?? [];
    const next: Record<LocalChainStage, string | null> = {
      designer: null,
      printer: null,
      workshop: null,
    };
    for (const a of rows as LineProviderAssignmentRow[]) {
      next[a.stage] = a.provider?.id ?? null;
    }
    setStageAssignments(next);
  }, [open, assignmentsQuery.data]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('You must be signed in.');

      // 1. Update production_path on the order line.
      const { error: pathErr } = await supabase
        .from('order_lines')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .update({ production_path: path } as any)
        .eq('id', lineId);
      if (pathErr) throw pathErr;

      if (path === 'eu') {
        // 2a. Persist EU provider; clear any leftover stage assignments.
        const { error: euErr } = await supabase
          .from('order_lines')
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .update({ assigned_provider_id: euProviderId } as any)
          .eq('id', lineId);
        if (euErr) throw euErr;
        const { error: clearErr } = await supabase
          .from('line_provider_assignments')
          .delete()
          .eq('order_line_id', lineId);
        if (clearErr) throw clearErr;
      } else {
        // 2b. Clear the EU single-provider slot, then upsert each stage.
        const { error: clearEuErr } = await supabase
          .from('order_lines')
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .update({ assigned_provider_id: null } as any)
          .eq('id', lineId);
        if (clearEuErr) throw clearEuErr;
        for (const stage of lineProviderAssignmentQueries.LOCAL_CHAIN_STAGES) {
          await lineProviderAssignmentQueries.setLineStageProvider(supabase, {
            orderLineId: lineId,
            stage,
            providerId: stageAssignments[stage],
            assignedBy: user.id,
          });
        }
      }
    },
    onSuccess: () => {
      onSaved();
      onOpenChange(false);
    },
    onError: (e) =>
      alert(`Could not update route: ${e instanceof Error ? e.message : String(e)}`),
  });

  const providers: AdminProviderRow[] = providersQuery.data ?? [];
  const euProviders = providers.filter((p) => p.provider_type === 'eu_end_to_end');
  const designerProviders = providers.filter((p) => p.provider_type === 'local_designer');
  const printerProviders = providers.filter((p) => p.provider_type === 'local_printer');
  const workshopProviders = providers.filter((p) => p.provider_type === 'local_workshop');

  const loading = providersQuery.isLoading || (path === 'local' && assignmentsQuery.isLoading);

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="md">
        <ModalHeader>
          <ModalTitle>Production route — {orderNumber}</ModalTitle>
          <ModalDescription>
            Choose how this order is produced. Saving updates the order line and
            its provider assignments.
          </ModalDescription>
        </ModalHeader>
        <div className="px-6 pb-4 space-y-5">
          {/* Route radio */}
          <fieldset className="space-y-2">
            <legend className="text-xs font-semibold uppercase tracking-wide text-text-muted">
              Route
            </legend>
            <div className="flex items-center gap-2">
              {(['eu', 'local'] as const).map((opt) => (
                <label
                  key={opt}
                  className={[
                    'inline-flex items-center gap-2 h-9 px-3 rounded-md border text-sm cursor-pointer transition-colors',
                    path === opt
                      ? 'bg-brand-700 text-white border-brand-700'
                      : 'bg-surface-elevated text-text-secondary border-border-default hover:bg-sand-50',
                  ].join(' ')}
                >
                  <input
                    type="radio"
                    name="route"
                    value={opt}
                    checked={path === opt}
                    onChange={() => setPath(opt)}
                    className="sr-only"
                  />
                  {opt === 'eu' ? 'EU end-to-end' : 'Local chain'}
                </label>
              ))}
            </div>
          </fieldset>

          {loading ? <Skeleton className="h-32 w-full" /> : null}

          {!loading && path === 'eu' ? (
            <div className="space-y-1.5">
              <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                EU end-to-end provider
              </div>
              <select
                value={euProviderId ?? ''}
                onChange={(e) => setEuProviderId(e.target.value || null)}
                className="w-full h-10 rounded-md border border-border-default bg-surface-elevated px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                <option value="">— Unassigned —</option>
                {euProviders.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {!loading && path === 'local' ? (
            <div className="space-y-3">
              <StageSelect
                label="Designer (shell CAD)"
                value={stageAssignments.designer}
                providers={designerProviders}
                providerTypeLabel="local_designer"
                onChange={(id) =>
                  setStageAssignments((prev) => ({ ...prev, designer: id }))
                }
              />
              <StageSelect
                label="Local printer (shell)"
                value={stageAssignments.printer}
                providers={printerProviders}
                providerTypeLabel="local_printer"
                onChange={(id) =>
                  setStageAssignments((prev) => ({ ...prev, printer: id }))
                }
              />
              <StageSelect
                label="Workshop (finishing)"
                value={stageAssignments.workshop}
                providers={workshopProviders}
                providerTypeLabel="local_workshop"
                onChange={(id) =>
                  setStageAssignments((prev) => ({ ...prev, workshop: id }))
                }
              />
            </div>
          ) : null}
        </div>
        <div className="px-6 pb-6 pt-2 flex items-center justify-end gap-2 border-t border-border-subtle">
          <Button
            variant="outline"
            size="md"
            onClick={() => onOpenChange(false)}
            disabled={saveMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || loading}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save route'}
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
}

function StageSelect({
  label,
  value,
  providers,
  providerTypeLabel,
  onChange,
}: {
  label: string;
  value: string | null;
  providers: AdminProviderRow[];
  providerTypeLabel: 'local_designer' | 'local_printer' | 'local_workshop';
  onChange: (id: string | null) => void;
}) {
  return (
    <div className="space-y-1.5">
      <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
        {label}
      </div>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        className="w-full h-10 rounded-md border border-border-default bg-surface-elevated px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
      >
        <option value="">— Unassigned —</option>
        {providers.length === 0 ? (
          <option value="" disabled>
            (no {providerTypeLabel} providers configured)
          </option>
        ) : null}
        {providers.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
    </div>
  );
}
